import { stepBar, stepDate } from '../enquiry-steps'
import { owedSteps } from '../owed-view'
import { meaningIcon } from '../meaning-icon'
import { owedBreakdown, paymentBreakdownMessage, paymentBreakdownPrefix } from '../owed-breakdown'
import { keepAboveKeyboard } from '../sheet-keyboard'
import { addressLine } from '../address-words'
import { contactButton } from '../contact-tile'
import { invoicesHere, openInvoice, openNewInvoice, payInvoiceFromOwed, renderInvoiceStep, stateChip, type InvoiceState } from '../invoices'
import { invoiceAfterEachJob } from '../commercial'
import { businessHint, businessReason, type HintCustomer } from '../business-hint'
import { currencySymbol, currencyWord } from '../round-config'
import { allPaidUp, flyToPaidToday, nudgeSlideOnce, owedBanner, owedLook, owedMoment, paidLook, paidMoment, slideToPay, type PaidWay } from '../paid-moment'
import '../owed.css'
import { styleSelect } from '../styled-select'
import type { WordingKey } from '../business-settings'
import { owedLayout, type OwedLayout } from '../owed-layout'
import { api, ApiError, changeNow, mutate, readCache, getQueue, onQueueChange, queuePartition, type Ack, type MoneyNotice } from '../api'
import { countBetween, slideAway, landFlash, reducedMotion } from '../motion'
import { routeGen } from '../route-state'
import { setOwedBadge } from '../owed-badge'
import { offerUndo, undoPatch, type UndoLink } from '../undo'
import { priceButton } from '../price-edit'
import { accountChaseMessage, chaseCountOf, chaseMessage, chaseStage, chaseUncounted, nextRung, paymentTodayMessage } from '../chase-message'
import { collectingWords, fromCredit, toCollect, type BroughtAccount, type OwedBrought, type UnconfirmedCash } from '../brought'
import { askCashTaken } from '../cash-ask'
import { firstName } from '../late-message'
import { linkFor } from '../contact'
import { businessSettings, visibleTime, wordMessage } from '../business-settings'
import { bottomSheet } from '../due-bar'
import { sendControl } from '../contact-ui'
import { bindEmailSend } from '../email-send'
import { hasExtraJobs, loadRoundJobs, visitJobLabel } from '../job-names'
import { sendAllOn } from '../send-all-on'
import type { SendAllFacts } from '../send-all'
import { waSendWords } from '../wa-template-words'
import { onAPhone, waNumber } from '../rebook-message'
import { wordsPrint } from '../send-all-words'
import { paymentWords, visitWords } from '../visit-words'
import { paymentLabel } from '../payment'
import {
  OWED_FILTERS,
  accountStage,
  daysAgoLabel,
  daysOldOf,
  owedChip,
  owedCounts,
  owedStageOf,
  invoiceToSend,
  matchesOwed,
  filterOwed,
  owedEmptyText,
  paymentMessageDue,
  paymentMessageWaiting,
  type OwedFilter,
  type OwedPayload,
  type OwedStage,
} from '../owed-view'
import type { Clean, Customer } from '../types'
import { getMe } from '../me'
import { addDaysISO, clear, dayLabel, el, loadError, money, parseMoney, shortDate, telHref, toast, todayISO, shownVisitTime, noteDate } from '../ui'
import { lineDrawing, type Drawing } from '../picture-tiles'
import { tidyAddress } from '../address-words'

// Owed's tiles' pictures (design B, 23 Oct 2026).
// Waiting (money owed after a reminder) takes the Waiting on them meaning square; To chase is a send, so it keeps its bubble
// (Liam, 1 Nov 2026: the new icons everywhere the meaning is the same).
const OWED_TILE_DRAWINGS: Record<string, Drawing> = { send: 'bubble', call: 'phone', unrecorded: 'question' }

// Owed — every done-but-unpaid clean in one place.
//
// Before this screen the question "who owes us money?" had no answer in the
// app: unpaid state existed per clean, but reaching it meant opening customers
// one at a time, so chasing happened outside the system and the receivable
// went stale silently. This is the desk screen for that job, and it carries
// the actions (mark paid, mark chased) so a chase can be closed from the row
// it started on.
//
// The row is a tile (2026-09-12, Liam's approved mockup): who and how much on
// the first line, when and where on the second with one status chip, then ONE
// main button for the stage the chase is at. Everything rarely needed — call,
// counting a chase sent another way, the customer's page, and "this visit
// wasn't done" — moved under More, because five buttons side by side said
// nothing about which one to press.

let payload: OwedPayload | null = null
// Two reads of /api/owed can be in flight at once: marking a row paid fires
// one while the mount's is still out. routeGen only catches a change of
// SCREEN, so without a sequence the OLDER answer can land last and put a
// clean that has just been settled back on the chase list (the same guard
// schedule.ts and rebook.ts use).
let requestSequence = 0
// The headline total before a Paid tap, so the next draw can count down from it.
let countFromPence: number | null = null
// When a payment was last saved here: if it leaves nobody owing, the list says All paid up (once), on whichever redraw
// comes within a few seconds (a background reload can land before the paid row has slid away).
let justPaidAt = 0
// What is typed in the search box, kept while the app is open.
let filterText = ''
// Which chip is selected. Module-level like filterText: it survives a
// re-render (every tap on the screen re-renders it) and resets on reload.
let filterMode: OwedFilter = 'all'
let selected: string | null = null
// A record another screen asked to open (openOwedFor): picked out and scrolled to on the next New draw, then cleared.
let landOn: string | null = null
let sortMode = 'newest'
const reminderDrafts = new Map<string, string>()
let queueBound = false
let mountedPartition: string | null = null
let mountedGeneration = -1
const onOwed = (): boolean => routeGen === mountedGeneration && queuePartition() === mountedPartition && window.location.hash === '#/owed'
let layoutBreakpoint: MediaQueryList | null = null
function bindLayoutBreakpoint(): void {
  if (layoutBreakpoint || typeof matchMedia !== 'function') return
  layoutBreakpoint = matchMedia('(max-width: 850px)')
  layoutBreakpoint.addEventListener('change', () => {
    if (!onOwed() || !selected || owedLayout() === 'original') return
    const page = document.querySelector<HTMLElement>('.owed-page')
    const card = Array.from(page?.querySelectorAll<HTMLElement>('.owed-selected-card') ?? []).find((c) => c.dataset.owedCardKey === selected && !c.hidden)
    const button = Array.from(page?.querySelectorAll<HTMLButtonElement>('.owed-queue-button') ?? []).find((b) => b.dataset.select === selected)
    const target = layoutBreakpoint?.matches ? button?.parentElement : page?.querySelector<HTMLElement>('.owed-detail-slot')
    if (!card || !target || card.parentElement === target) return
    const focused = document.activeElement as HTMLElement | null
    const restoreFocus = focused?.closest('.owed-selected-card') === card
    card.classList.remove('owed-card-arriving')
    target.querySelector('.owed-selection-hint')?.remove()
    target.append(card)
    if (restoreFocus) focused?.focus({ preventScroll: true })
  })
}
// Original is the Owed screen as it was before the 7 Oct redesign (Liam, 8 Oct 2026: "not the one we had a couple
// of iterations ago that we were using"): its own layout, drawn by renderClassic, with today's money code underneath.
let classic = false
// Calls are attached to this debt, never inferred from the customer's other calls.
function debtSteps(container: HTMLElement, row: { version: number; chased: 0 | 1; chase_count?: number | null; chased_on?: string | null; chase_called_at?: string | null; payment_status: Clean['payment_status'] }, path: string, today: string, held = '', canUndoSend = true): void {
  const generation = routeGen, partition = queuePartition()
  const current = (): boolean => onOwed() && generation === routeGen && partition === queuePartition()
  const steps = owedSteps(row, today, held)
  const n = chaseCountOf(row)
  const save = async (body: Record<string, unknown>): Promise<boolean> => {
    if (!current()) return false
    const ack = await mutate({ label: 'Go back a payment step', method: 'PATCH', path, body: { version: row.version, ...body }, onAck: () => { if (current()) void load() }, onConflict: () => { if (current()) void load() } })
    if (!current()) return false
    alert4xx(ack)
    return ack.ok
  }
  for (const step of steps) {
    if (step.when) { step.when = stepDate(step.when); step.detail = `${step.key === 'call' ? 'Called' : 'Sent'} ${step.when}` }
    if (step.state === 'done' && step.key === 'call') { step.goBackLabel = 'Go back a step'; step.goBack = () => save({ chase_called_at: null }) }
    else if (step.state === 'done' && step.key === `message-${n}` && !row.chase_called_at && canUndoSend) {
      step.goBack = () => save(path.includes('brought-accounts') ? { chase_count: n - 1, chased_on: null } : chaseUncounted(row))
    }
  }
  const bar = stepBar(steps, { label: 'Payment chasing steps', ended: row.payment_status === 'paid' })
  container.append(bar)
}
function debtCall(tel: string, first: string, path: string, version: number): HTMLAnchorElement {
  const generation = routeGen, partition = queuePartition()
  const current = (): boolean => onOwed() && generation === routeGen && partition === queuePartition()
  const link = el('a', { href: tel, class: 'owed-call-btn owed-call-main', 'aria-label': `Call ${first || 'them'}` }, lineDrawing('phone', 20), `Call ${first || 'them'}`)
  // The Original layout has no step bar, so a call there just rings: nothing is saved that could not be seen or taken back.
  if (classic) return link
  link.addEventListener('click', e => {
    if (!current() || link.dataset.busy) { e.preventDefault(); return }
    link.dataset.busy = '1'
    void mutate({ label: `Called ${first || 'them'}`, method: 'PATCH', path, body: { version, chase_called_at: new Date().toISOString() }, onAck: () => { if (current()) void load() }, onConflict: () => { if (current()) void load() } }).then(ack => { if (current()) alert4xx(ack); delete link.dataset.busy })
  })
  return link
}
const callLabel = (first: string): string => classic ? `Call ${first || 'them'}` : 'Call'
// Unsaved typing on screen: a cash amount being asked for, or an opened part-paid/correction form. Switching layout
// redraws Owed, so it waits for these as it waits for a save. (The standing cash-confirm rows are not counted.)
const formOpen = (page: HTMLElement): boolean => !!page.querySelector('.cash-ask') || Array.from(page.querySelectorAll<HTMLElement>('.owed-inline')).some((n) => !n.hidden && !n.classList.contains('owed-cash-row'))
const combinedTotal = (): number | null => payload ? payload.total_pence + (payload.brought?.total_pence ?? 0) : null

interface PaymentEffect { forward_key: string }
// Thanks for paying (Liam, 26 Oct 2026, with the Google reviews link): once someone is marked paid, "Send a thank you"
// beside Undo, only when Your business has a reviews link. It opens the message and its send button; nothing goes by
// itself.
function thanksLink(cl: Clean): UndoLink | undefined {
  if (!businessSettings().reviews_link?.trim()) return undefined
  return { label: 'Send a thank you', act: () => {
    const who = cl.customer_name ?? 'them'
    const words = wordMessage('paid_thanks', { 'first name': firstName(cl.customer_name), 'your name': businessSettings().chase_signoff })
    const sheet = bottomSheet(`Thank ${who}`, () => {})
    sheet.dialog.classList.add('owed-thanks-sheet')
    sheet.head.append(el('h3', {}, `Thank ${firstName(cl.customer_name) || who}`))
    const control = sendControl({ id: cl.customer_id, name: who, version: cl.customer_version, phone: cl.customer_phone ?? null, email: cl.customer_email ?? null, message_by: cl.customer_message_by ?? null }, words, `Thank you from ${businessSettings().business_name}`, {
      onSend: () => { toast(`Thank you sent to ${who}`); sheet.close() }, changed: () => {}, onConflict: () => {} })
    sheet.body.append(el('p', { class: 'owed-bubble' }, words),
      el('a', { href: '#/your-messages?m=paid_thanks', class: 'link-button' }, 'Change wording'), control.root)
  } }
}

function paymentUndo(ack: Ack, name: string, id: number, text: string, link?: UndoLink): void {
  if (!ack.ok || !onOwed()) return // Never issue an inverse before the durable forward ACK.
  const effect = (ack.row as { _payment_effect?: PaymentEffect } | undefined)?._payment_effect
  if (!effect) return
  const offeredPartition = queuePartition()
  const offeredGeneration = routeGen
  offerUndo(text, () => {
    if (queuePartition() !== offeredPartition || routeGen !== offeredGeneration || !onOwed()) { window.alert('Undo was not applied because the signed-in round changed.'); return }
    void mutate({ label: `Undo payment: ${name}`, method: 'POST', path: `/api/cleans/${id}/payment-undo`, body: { forward_key: effect.forward_key },
      onAck: () => { if (routeGen !== offeredGeneration || queuePartition() !== offeredPartition || !onOwed()) return; selected = `visit:${id}`; void load().then(() => { const card = document.querySelector<HTMLElement>(`[data-record="visit:${id}"]`); if (card) landFlash(card) }) },
      onConflict: () => void load(),
    }).then((inverse) => { if (!inverse.ok && inverse.status >= 400) window.alert(`Undo was refused: ${inverse.error ?? 'Review the current figures.'}`) })
  }, link)
}

function isPending(paths: string[]): boolean {
  return getQueue().some((q) => paths.some((path) => q.path === path || q.path.startsWith(`${path}/`)))
}
function pendingCards(): void {
  for (const card of document.querySelectorAll<HTMLElement>('.owed-workspace [data-paths]')) {
    const pending = isPending(JSON.parse(card.dataset.paths!) as string[])
    card.classList.toggle('owed-pending', pending)
    card.setAttribute('aria-busy', String(pending))
    const status = card.querySelector<HTMLElement>('.owed-pending-status')
    if (status) status.hidden = !pending
  }
}

// Both editable presentations update their own Send, including after the breakdown redraws the card.
function relinkPaymentMessage(control: ReturnType<typeof sendControl>, target: Parameters<typeof sendControl>[0], words: string, subject: string): void {
  if (!control.link) return
  const href = linkFor(control.send.way, target, words, subject).href
  // Update the same attribute the hand-off reads on click, on the control belonging to this preview.
  if (href) { control.link.href = href; control.link.setAttribute('href', href) }
}

// Preview is local. Only using the channel link records a chase. The shared
// control still owns reachability, consent, preferences and intentional clicks.
// Shown open under "Reminder to send", as a Messages card shows its message (Liam, 8 Oct 2026: Owed looks like Messages).
function reminderPreview(key: string, body: string, control: ReturnType<typeof sendControl>, target: Parameters<typeof sendControl>[0], subject: string, wording?: WordingKey, title = 'Reminder to send'): HTMLElement {
  const box = el('div', { class: 'owed-reminder' })
  const head = el('div', { class: 'owed-reminder-head' }, el('span', {}, title))
  // Change this reminder's standard wording for every customer (More > Your messages, 8 Oct 2026).
  if (wording) head.append(el('a', { href: `#/your-messages?m=${wording}`, class: 'link-button rebook-wording', title: 'Change the wording of every reminder like this one' }, 'Change wording'))
  box.append(head)
  const text = el('textarea', { 'aria-label': 'Payment reminder', rows: '6' })
  text.value = reminderDrafts.get(key) ?? body
  const refresh = (): void => {
    reminderDrafts.set(key, text.value)
    relinkPaymentMessage(control, target, text.value, subject)
  }
  text.addEventListener('input', refresh)
  box.append(text, el('p', { class: 'owed-help' }, !onAPhone()
    ? 'Send it in your app. If Did it send? appears, answer it here.'
    : 'On a phone, opening your messaging app counts a chase. On a computer, confirm it after sending.'))
  return box
}

// The payment message as the customer will get it (Liam, 26 Oct 2026, design B: "our brains have been trained with those
// kind of text messages"; https://claude.ai/artifact/KAFU2uE4jdQjyBri4cBqFL): a green bubble, tapped to change its words
// (the same draft as the box it replaces, so the send link follows), "Change wording" for every customer's.
function bubblePreview(key: string, body: string, control: ReturnType<typeof sendControl>, target: Parameters<typeof sendControl>[0], subject: string, wording: WordingKey, first: string): HTMLElement {
  const box = el('div', { class: 'owed-bubble-box' })
  // Nothing to send it with yet (no mobile, no email, Don't message): the message, not "what they get".
  box.append(el('div', { class: 'owed-reminder-head' }, el('span', {}, control.link ? `What ${first} gets` : 'The message'),
    el('a', { href: `#/your-messages?m=${wording}`, class: 'link-button rebook-wording', title: 'Change the wording of every message like this one' }, 'Change wording')))
  const words = (): string => reminderDrafts.get(key) ?? body
  const relink = (): void => {
    relinkPaymentMessage(control, target, words(), subject)
  }
  const bubble = el('button', { type: 'button', class: `owed-bubble owed-bubble-${control.send.way}`, 'aria-label': `The message to ${first}. Tap to change its words.` }, words())
  // An email shows its subject above it, as their inbox will.
  const subjectLine = control.send.way === 'email' ? el('span', { class: 'owed-bubble-subject' }, subject) : null
  if (subjectLine) box.append(subjectLine)
  const hint = el('span', { class: 'owed-bubble-hint' }, 'Tap it to change the words')
  bubble.addEventListener('click', () => {
    const text = el('textarea', { class: 'owed-bubble-edit', 'aria-label': `The message to ${first}`, rows: '8' }) as HTMLTextAreaElement
    text.value = words()
    text.addEventListener('input', () => { reminderDrafts.set(key, text.value); relink() })
    const doneBtn = el('button', { type: 'button', class: 'link-button owed-bubble-done' }, 'Done')
    doneBtn.addEventListener('click', () => { bubble.textContent = words(); text.replaceWith(bubble); doneBtn.replaceWith(hint) })
    bubble.replaceWith(text); hint.replaceWith(doneBtn)
    text.focus()
  })
  box.append(bubble, hint)
  return box
}

function alert4xx(ack: Ack): void {
  if (!onOwed()) return
  if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
}

// ---- the More menu ---------------------------------------------------------

// One menu open at a time, closed by an outside tap, Escape or navigation —
// the same contract the account menu in main.ts keeps. Held here rather than
// on the row because "only one" is a property of the screen.
let openMenu: { menu: HTMLElement; close: () => void } | null = null
let menuListeners = false

// Opens upwards when there is no room below it on the screen (the card is pinned beside the list on a computer, so a
// menu running past the bottom of the screen could not be scrolled into view: bug hunt 8 Oct 2026, owed-today #1).
function placeMenu(menu: HTMLElement, button: HTMLElement): void {
  menu.classList.remove('owed-menu-up')
  if (typeof window.innerHeight !== 'number' || typeof menu.getBoundingClientRect !== 'function') return
  const box = menu.getBoundingClientRect()
  const top = button.getBoundingClientRect().top
  if (box.bottom > window.innerHeight - 8 && top - box.height - 8 > 0) menu.classList.add('owed-menu-up')
}

function closeMenu(): void {
  const open = openMenu
  openMenu = null
  open?.close()
}

function watchMenus(): void {
  if (menuListeners) return
  menuListeners = true
  document.addEventListener('click', (e) => {
    if (openMenu && !openMenu.menu.contains(e.target as Node)) closeMenu()
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu()
  })
  window.addEventListener('hashchange', closeMenu)
}

// A menu line: the action, with the explanation underneath where one is
// needed. Both are one tap target, never two.
function menuText(label: string, sub?: string): HTMLElement {
  const wrap = el('span', { class: 'owed-mi-text' }, label)
  if (sub) wrap.append(el('small', {}, sub))
  return wrap
}

// ---- "This visit wasn't done" ----------------------------------------------

// Where the customer goes next. Messages (Rebook) lists a customer whose last
// DONE clean is overdue against their cycle and who has no confirmed future
// visit — so if the visit being undone was their ONLY done clean they have no
// last clean at all and will NOT appear there; they sit under "Past bookings
// to review" on the diary instead. Answered from the customers cache; null
// means the cache cannot say, and the wording stays neutral rather than
// promising the wrong screen.
function hasAnotherDoneClean(cl: Clean): boolean | null {
  const cached = readCache<{ customers: Customer[] }>('customers')
  const rows = cached?.data?.customers
  if (!Array.isArray(rows)) return null
  const customer = rows.find((c) => c.id === cl.customer_id)
  if (!customer || customer.last_clean_id === undefined || customer.last_clean_id === null) return null
  // A later done clean than this one: certainly another.
  if (customer.last_clean_id !== cl.id) return true
  // This IS the latest done clean (the usual case). The list carries no count
  // of done cleans, but the cycle estimate exists only when there are at
  // least two — so a number here means an earlier one exists.
  if (typeof customer.cycle_estimate_days === 'number') return true
  // One done clean, or a cached list from before the estimate existed: the
  // list cannot say, and the wording must not name the wrong screen.
  return null
}

function landingWords(lands: boolean | null): string {
  if (lands === true) return ' · now in Messages'
  if (lands === false) return ' · see Past bookings on the diary'
  return ''
}

// The visit was marked done in error — the cleaner never moved the date — so
// nothing is owed and the customer needs re-booking. One PATCH: state back to
// booked, with a line on the note saying where the correction came from. The
// server resets the payment fields and the chase ladder on that transition
// and withdraws the next visit it auto-booked off the done clean, so this is
// the whole of it. No confirm dialog: the app uses an Undo bar for anything
// reversible, and this is reversible.
function markNotDone(cl: Clean, item: HTMLElement, container: HTMLElement): void {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const actionCurrent = (): boolean => onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition
  item.setAttribute('disabled', 'disabled')
  container.classList.add('cell-pending')
  const name = cl.customer_name ?? 'visit'
  const lands = landingWords(hasAnotherDoneClean(cl))
  void mutate({
    label: `Not done: ${cl.customer_name ?? ''} ${cl.date}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: cl.version, state: 'booked', note_append: `Not done: marked from Owed ${noteDate(todayISO())}` },
    onAck: () => { if (actionCurrent()) void load() },
    onConflict: () => { if (actionCurrent()) void load() },
  }).then((ack) => {
    if (!actionCurrent()) return
    if (!ack.ok && ack.status >= 400 && ack.status < 500) {
      item.removeAttribute('disabled')
      container.classList.remove('cell-pending')
    }
    alert4xx(ack)
    undoPatch({
      text: `Marked ${name}'s ${shortDate(cl.date)} visit not done${lands}`,
      label: `Undo not done: ${cl.customer_name ?? ''} ${cl.date}`,
      path: `/api/cleans/${cl.id}`,
      ack,
      version: cl.version,
      // state done first, so the payment fields are legal again on the way
      // back; the note goes back to what it was before the line was added.
      body: {
        state: 'done',
        payment_status: cl.payment_status,
        payment_method: cl.payment_method ?? null,
        chased: cl.chased,
        chase_count: chaseCountOf(cl),
        chased_on: cl.chased_on ?? null,
        note: cl.note ?? null,
        // A part done (0070): done again as that part at its price, the rest booked again (Not done put the whole job
        // back and took the rest out).
        ...(cl.part && cl.rest_of == null ? { part_done: { part: cl.part, ...(typeof cl.amount_pence === 'number' ? { price_pence: cl.amount_pence } : {}) } } : {}),
      },
      after: () => void load(),
    })
  })
}

// ---- the row ---------------------------------------------------------------

// A commercial customer's work (0080; docs/plans/commercial/AGREED.md 6, 8): asked for by its invoice, never the payment
// message or a chase. An invoice made and not sent yet is opened again ("DWC-0014 · Not sent yet"), never a second.
type LiveInvoice = { live_invoice_id?: number | null; live_invoice_number?: string | null }
// The customer's newest WhatsApp since the visit's last chase (functions/api/owed.ts wa_since_chase), or null.
function sinceChase(cl: Clean): { id: number; at: string; words: string } | null {
  if (!cl.wa_since_chase) return null
  try {
    const v = JSON.parse(cl.wa_since_chase) as { id?: unknown; at?: unknown; words?: unknown }
    return typeof v.id === 'number' && typeof v.at === 'string' ? { id: v.id, at: v.at, words: typeof v.words === 'string' ? v.words : '' } : null
  } catch {
    return null
  }
}

// A payment text from Send all (0093) that Roundly isn't sure went: no further reminder until the owner says. "It didn't
// arrive" puts its reminder back on the visit (the server, by the send's own record), so the Chase button comes back.
function unsureTextBlock(cl: Clean): HTMLElement {
  const uid = (cl as Clean & { wa_attempt?: string | null }).wa_attempt ?? ''
  const box = el('div', { class: 'owed-held' }, el('p', { class: 'owed-held-note' }, "Not sure Roundly's text went. Check their chat, then say."))
  const row = el('div', { class: 'owed-held-acts' })
  for (const [label, arrived] of [['It arrived', true], ["It didn't arrive", false]] as const) {
    const b = el('button', { type: 'button', class: 'btn-secondary' }, label) as HTMLButtonElement
    b.addEventListener('click', async () => {
      b.disabled = true
      try { await changeNow('POST', `/api/chase-sends/${uid}`, { arrived }); toast(arrived ? 'Kept as sent.' : "Its reminder is back: send it again when you're ready.") }
      catch (err) { toast((err as Error).message) }
      void load()
    })
    row.append(b)
  }
  box.append(row, el('a', { class: 'owed-wa-since', href: `#/chats?customer=${cl.customer_id}` }, 'Open their chat ›'))
  return box
}

function heldBlock(cl: Clean, since: { id: number; at: string; words: string } | null, paused: boolean): HTMLElement {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const actionCurrent = (): boolean => onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition
  const box = el('div', { class: 'owed-held' })
  if (since) {
    const words = since.words.replace(/\s+/g, ' ').trim()
    box.append(el('a', { class: 'owed-wa-since', href: `#/chats?customer=${cl.customer_id}` }, `New WhatsApp since you chased${words ? `: "${words.length > 60 ? `${words.slice(0, 57)}…` : words}"` : ''} ›`))
    if (!paused) box.append(el('p', { class: 'owed-held-note' }, 'The next chase waits while this shows.'))
  }
  if (paused) box.append(el('p', { class: 'owed-held-note owed-paused' }, 'Chasing paused'))
  // Each save names the WhatsApp shown here, so one that came in after this was drawn still shows afterwards.
  const save = (button: HTMLElement, label: string, body: Record<string, unknown>): void => {
    button.setAttribute('disabled', 'disabled')
    void mutate({
      label: `${label}: ${cl.customer_name ?? ''} ${cl.date}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, ...body, ...(since ? { wa_seen: since.id } : {}) },
      onAck: () => { if (actionCurrent()) void load() },
      onConflict: () => { if (actionCurrent()) void load() },
    }).then((ack) => {
      if (!actionCurrent()) return
      if (!ack.ok && ack.status >= 400 && ack.status < 500) button.removeAttribute('disabled')
      alert4xx(ack)
    })
  }
  const acts = el('div', { class: 'owed-held-acts' })
  const pause = el('button', { type: 'button', class: 'btn-secondary owed-pause' }, paused ? 'Resume chasing' : 'Pause chasing')
  pause.addEventListener('click', () => save(pause, paused ? 'Resume chasing' : 'Pause chasing', { chase_paused: !paused }))
  acts.append(pause)
  // Read it and still want to chase (Astra's code review: the owner's own decision after reading, without pausing first).
  if (since && !paused) {
    const anyway = el('button', { type: 'button', class: 'link-button owed-chase-anyway' }, "I've read it, chase anyway")
    anyway.addEventListener('click', () => save(anyway, 'Read, chase anyway', {}))
    acts.append(anyway)
  }
  box.append(acts)
  return box
}

function invoiceActOf(cl: Clean): { label: string; run: () => void } {
  const live = cl as Clean & LiveInvoice
  if (live.live_invoice_id) return { label: `${live.live_invoice_number ?? 'Invoice'} · Not sent yet`, run: () => openInvoice(live.live_invoice_id!, { phone: cl.customer_phone ?? null, done: () => void load() }) }
  return { label: 'Send invoice', run: () => openNewInvoice(cl.customer_id, { dayOf: cl.id }, { done: () => void load() }) }
}
function invoiceAction(cl: Clean): HTMLElement {
  const act = invoiceActOf(cl)
  const b = el('button', { type: 'button', class: 'btn-primary owed-invoice' }, act.label)
  b.addEventListener('click', () => act.run())
  return b
}
// What the Done pop-up knows of a customer's Commercial switch: the visit's own copy (openDonePopup reads their page),
// else the customers this phone last read; isCommercial then lays a waiting change over either.
function commercialFacts(cl: Clean): { id: number; commercial?: number | null; invoice_mode?: string | null } {
  const own = cl as Clean & { customer_commercial?: number | null; customer_invoice_mode?: string | null }
  if (own.customer_commercial !== undefined) return { id: cl.customer_id, commercial: own.customer_commercial, invoice_mode: own.customer_invoice_mode ?? null }
  const cached = readCache<{ customers?: (Customer & { commercial?: number; invoice_mode?: string | null })[] }>('customers')?.data?.customers?.find((c) => c.id === cl.customer_id)
  return { id: cl.customer_id, commercial: cached?.commercial ?? null, invoice_mode: cached?.invoice_mode ?? null }
}

// Looks like a business (Liam, 26 Oct 2026, design B: https://claude.ai/artifact/2idUxH4jyrEdpi8D15jAiM; business-hint.ts;
// Astra's check of the logic the same day). A row that would ask for the money by message, for a customer whose name or
// address says business ("Peter Dyer Photographs Ltd"), offers Send invoice beside the payment message. Never a row that
// is held (a WhatsApp since the chase, or chasing paused: Astra 8), nor one with nothing left to pay (Astra 7).
type HintFacts = { reason: string; customer: HintCustomer & { version: number } }
// Refused for good (4xx) vs kept on this phone to send again (no signal, or a server error the queue retries: Sol 3).
const refusedAck = (ack: Ack): boolean => !ack.ok && ack.status >= 400 && ack.status < 500
// A change to this customer still on its way: the question waits for it (Sol 2).
const customerPending = (id: number): boolean => isPending([`/api/customers/${id}`])
const HINT_STAGES = ['today', 'send', 'waiting', 'call']
function hintFor(cl: Clean, stage: string): HintFacts | null {
  if (!HINT_STAGES.includes(stage) || cl.payment_status !== 'unpaid' || (toCollect(cl) ?? 0) <= 0) return null
  if ((cl as Clean & { carryover?: number | null }).carryover === 1 || sinceChase(cl) || cl.chase_paused_at) return null
  const own = cl as Clean & { customer_commercial?: number | null; customer_invoice_mode?: string | null; customer_business_hint_dismissed?: number | null }
  if (typeof cl.customer_version !== 'number') return null
  const customer = { id: cl.customer_id, version: cl.customer_version, name: cl.customer_name ?? null, address: cl.customer_address ?? null,
    commercial: own.customer_commercial ?? 0, invoice_mode: own.customer_invoice_mode ?? null, business_hint_dismissed: own.customer_business_hint_dismissed ?? 0 }
  const reason = businessHint(customer)
  return reason ? { reason, customer } : null
}
// Send invoice: Commercial on for them, so this visit (and their next jobs) moves to "Invoice to send", shown and glowing,
// where its own lemon button makes the invoice; Undo stays reachable because no pop-up opens (Astra 1, 2, 6, 16).
// Not a business / Keep payment messages: Roundly stops asking. Each waits for the save before saying it is done.
function answerHint(cl: Clean, facts: HintFacts, answer: 'invoice' | 'dismiss', button: HTMLElement): void {
  const name = cl.customer_name ?? 'They'
  const path = `/api/customers/${cl.customer_id}`
  const generation = routeGen
  if (customerPending(cl.customer_id)) { toast(`Still saving your last change for ${name}. Try again in a moment.`); return }
  for (const b of button.closest('.owed-business-area')?.querySelectorAll<HTMLButtonElement>('button') ?? []) b.disabled = true
  button.setAttribute('disabled', 'disabled')
  const body = answer === 'invoice' ? { commercial: 1 } : { business_hint_dismissed: 1 }
  void mutate({ label: answer === 'invoice' ? `Commercial: ${name}` : `Don't ask if business: ${name}`, method: 'PATCH', path, body: { version: facts.customer.version, ...body },
    onAck: () => { if (onOwed() && routeGen === generation) void load() } }).then((ack) => {
    if (!onOwed() || routeGen !== generation) return
    if (refusedAck(ack)) {
      toast(ack.status === 409 ? `${name}'s details were changed somewhere else. Have another look and try again.` : ack.error ?? "That didn't save. Try again.")
      void load()
      return
    }
    const waits = ack.ok ? '' : ' Saved on this phone: it sends when you have signal.'
    if (answer === 'invoice') { filterMode = 'all'; if (ack.ok) landOn = `visit:${cl.id}` }
    undoPatch({ text: answer === 'invoice' ? `${name} is a business now: Roundly offers their invoice after each job.${waits}` : `Roundly won't ask about ${name} again.${waits}`,
      label: `Undo: ${name}`, path, ack, version: facts.customer.version,
      body: answer === 'invoice' ? { commercial: 0 } : { business_hint_dismissed: 0 }, after: () => void load() })
    void load()
  })
}
// The question's words and its two quiet answers, shared by the row and the open card.
function hintLine(facts: HintFacts, amount: number | null): HTMLElement {
  return el('p', { class: 'owed-business-line' }, el('strong', {}, 'Looks like a business'), ` (it says ${facts.reason}). How do you want to ask for ${amount !== null ? money(amount) : 'it'}?`)
}
function hintDismissals(cl: Clean, facts: HintFacts): HTMLElement {
  const box = el('div', { class: 'owed-business-skip' })
  for (const label of ['Not a business', 'Keep payment messages']) {
    const b = el('button', { type: 'button', class: 'link-button' }, label)
    b.addEventListener('click', () => answerHint(cl, facts, 'dismiss', b))
    box.append(b)
  }
  return box
}
function hintInvoiceButton(cl: Clean, facts: HintFacts, tile: boolean): HTMLButtonElement {
  const b = (tile
    ? el('button', { type: 'button', class: 'owed-row-tile owed-business-invoice', 'aria-label': `Send ${cl.customer_name ?? 'them'} an invoice, now and after each job` },
      el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing('invoiceAdd', 19)),
      el('span', { class: 'owed-tile-words' }, el('strong', {}, 'Send invoice'), el('span', {}, 'Now and after each job')))
    : el('button', { type: 'button', class: 'btn-primary owed-business-invoice-btn' }, lineDrawing('invoiceAdd', 20), 'Send invoice, now and after each job')) as HTMLButtonElement
  b.addEventListener('click', () => answerHint(cl, facts, 'invoice', b))
  return b
}

function renderRow(cl: Clean, container: HTMLElement, today: string): void {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const actionCurrent = (): boolean => onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition
  clear(container)
  container.classList.add('owed-tile')
  const age = daysOldOf(cl.date, today)
  const first = firstName(cl.customer_name)
  const who = cl.customer_name ?? `#${cl.customer_id}`

  // Line one: who, and how much. The money is right-aligned with tabular
  // figures so a column of rows lines up and can be read down. New: a Messages card's top instead, the name and address
  // with the chase label on the right.
  // A commercial customer's work is asked for by its invoice: no chase chip on it (History keeps the chases; Astra, 21 Oct).
  const chip = invoiceToSend(cl) ? null : owedChip(cl, today, shortDate)
  const whoLink = el('a', { href: `#/customer/${cl.customer_id}`, class: 'owed-who' }, who)
  if (classic) container.append(whoLink)
  else container.append(el('div', { class: 'owed-card-top' }, el('div', { class: 'owed-card-who' }, whoLink, ...(cl.customer_address ? [el('span', { class: 'owed-card-addr' }, cl.customer_address)] : [])),
    ...(chip ? [el('span', { class: `owed-chip owed-chip-${chip.kind}` }, chip.text)] : [])))
  // Tap the amount to change it: a customer who paid a different price
  // (Liam 2026-09-28), or a visit with no price yet.
  // Owed keeps the box in place: a done visit's price is that visit's only (tidy-up 2 item 2, src/price-edit.ts).
  const price = priceButton(cl, {
    inline: true,
    className: cl.amount_pence === null || cl.amount_pence === undefined ? 'owed-amt owed-amt-none' : 'owed-amt',
    after: () => { countFromPence = combinedTotal(); void load() },
  })
  if (classic) container.append(price)
  else container.append(el('div', { class: 'owed-facts' },
    el('div', {}, el('small', {}, cl.payment_status === 'unknown' ? 'Payment not recorded' : 'Still to pay'), el('strong', {}, toCollect(cl) === null ? 'No price set' : money(toCollect(cl)))),
    el('div', {}, el('small', {}, 'Visit price'), price)))

  // Line two: when, how long ago, where — then the one status chip. Age is
  // the whole point of a chase list: a week is a reminder, three months is a
  // different conversation, so it goes red at 60 days.
  const meta = el('div', { class: 'owed-meta' })
  meta.append(el('span', {}, dayLabel(cl.date)))
  // Several jobs: which job the money is for, once the customer has more than one.
  const jobWord = hasExtraJobs(cl.customer_id) ? visitJobLabel(cl) : null
  if (jobWord) meta.append(el('span', { class: 'owed-dot' }, '·'), el('span', { class: 'owed-job' }, jobWord))
  meta.append(el('span', { class: 'owed-dot' }, '·'))
  meta.append(el('span', { class: age >= 60 ? 'owed-age owed-age-old' : 'owed-age' }, daysAgoLabel(age)))
  if (cl.customer_address && classic) {
    meta.append(el('span', { class: 'owed-dot' }, '·'))
    meta.append(el('span', { class: 'owed-addr' }, cl.customer_address))
  }
  if (chip && classic) meta.append(el('span', { class: `owed-chip owed-chip-${chip.kind}` }, chip.text))
  // Credit or cash already on it (0043): what is still to pay, so the chase asks for the right figure.
  const on = fromCredit(cl) + Math.max(0, cl.paid_in_pence ?? 0)
  if (on > 0 && cl.amount_pence !== null) {
    meta.append(el('span', { class: 'owed-dot' }, '·'), el('span', { class: 'owed-part' }, `${fromCredit(cl) ? `${money(fromCredit(cl))} from credit · ` : ''}${(cl.paid_in_pence ?? 0) > 0 ? `${money(cl.paid_in_pence)} paid · ` : ''}${money(toCollect(cl))} to pay`))
  }
  container.append(meta)

  // The note full width between the details and the buttons, so a long one
  // can never push the action bar into a different shape on some rows only.
  if (cl.note) container.append(el('div', { class: 'owed-note-box' }, cl.note))

  const actions = el('div', { class: 'owed-acts' })
  const tel = telHref(cl.customer_phone)
  const n = chaseCountOf(cl)
  const stage = cl.payment_status === 'unpaid' ? (invoiceToSend(cl) ? 'invoice' : chaseStage(cl, today)) : null
  // Done, not paid and no message yet (Owed's "Payment message to send", Liam 8 Oct 2026): the payment message in his
  // words (payment_today) instead of a reminder; sending it counts as the first chase. His words never say "today", so
  // the last week's visits get it too (Liam, 9 Oct 2026: "i thought the done? had a flow where ... it generated a
  // payment message"); older ones get reminder 1.
  const dueToday = !classic && paymentMessageWaiting(cl, today)

  const countChase = (via: string, button: HTMLElement): void => {
    button.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Chase ${n + 1}: ${cl.customer_name ?? ''} ${cl.date}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, chased: 1, chase_count: n + 1, chased_on: today },
      onAck: () => { if (actionCurrent()) void load() },
      onConflict: () => { if (actionCurrent()) void load() },
    }).then((ack) => {
    if (!actionCurrent()) return
      if (!ack.ok && ack.status >= 400 && ack.status < 500) button.removeAttribute('disabled')
      alert4xx(ack)
      undoPatch({ text: dueToday ? `Counted the payment message ${via} to ${cl.customer_name ?? 'visit'}` : `Counted a chase ${via} to ${cl.customer_name ?? 'visit'}`, label: `Undo chase: ${cl.customer_name ?? ''} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
        body: { chased: cl.chased, chase_count: n, chased_on: cl.chased_on ?? null }, after: () => void load() })
    })
  }

  // The Undo bar with no clock on it. The chase button counts on the tap, so
  // a tap taken only to READ the draft counts a chase that never went, and
  // the bar is ten seconds while WhatsApp is a trip out of the app — so the
  // way back has to live on the row itself.
  const uncountChase = (button: HTMLElement): void => {
    button.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Chase not sent: ${cl.customer_name ?? ''} ${cl.date}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, ...chaseUncounted(cl) },
      onAck: () => { if (actionCurrent()) void load() },
      onConflict: () => { if (actionCurrent()) void load() },
    }).then((ack) => {
    if (!actionCurrent()) return
      if (!ack.ok && ack.status >= 400 && ack.status < 500) button.removeAttribute('disabled')
      alert4xx(ack)
      undoPatch({ text: `Took a chase back off ${cl.customer_name ?? 'this visit'}`, label: `Redo chase: ${cl.customer_name ?? ''} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
        body: { chased: cl.chased, chase_count: n, chased_on: cl.chased_on ?? null }, after: () => void load() })
    })
  }

  // A WhatsApp from them since the last chase (0081, docs/plans/whatsapp-direct/PLAN.md item 67; picture 10): the line into
  // their chat, and the next chase waits while it shows. Pause chasing holds the chases (a lasting "Chasing paused") until
  // Resume chasing; a newer WhatsApp shows the line again. It never says the WhatsApp answers this debt.
  const since = sinceChase(cl)
  const paused = !!cl.chase_paused_at
  const held = cl.payment_status === 'unpaid' && stage !== 'invoice' && (since !== null || paused)

  // ONE main button, and only one. Which it is comes from the ladder: send the
  // next chase, nothing at all while the week runs (the chip on line two
  // carries that), or call once three messages have not worked. A commercial customer's work: its invoice.
  // Looks like a business: asked on the open card too, so it is there with the card open and on the Original layout (Astra 5).
  const hint = stage ? hintFor(cl, stage) : null
  if (hint) container.append(el('div', { class: 'owed-business-area owed-business-card' }, hintLine(hint, toCollect(cl)), hintInvoiceButton(cl, hint, false), hintDismissals(cl, hint)))
  // Send all (0093): a payment text Roundly isn't sure went holds the visit until the owner says whether it arrived.
  const unsureText = (cl as Clean & { chase_text_state?: string | null }).chase_text_state === 'unknown'
  if (!classic && cl.payment_status === 'unpaid' && !invoiceToSend(cl)) debtSteps(container, cl, `/api/cleans/${cl.id}`, today, unsureText ? 'Check whether the last text arrived' : paused ? 'Chasing paused' : since ? 'Read their new WhatsApp first' : '', !(cl as Clean & { wa_attempt?: string | null }).wa_attempt)
  if (unsureText && cl.payment_status === 'unpaid') {
    container.append(unsureTextBlock(cl))
  } else if (held) {
    container.append(heldBlock(cl, since, paused))
  } else if (stage === 'invoice') {
    actions.append(invoiceAction(cl))
  } else if (stage === 'send') {
    const rung = nextRung(n, age)
    // The customer's own way, else the round's usual (contact.ts). Their page is where the way is changed too;
    // the ▾ beside the button changes it here without leaving the row.
    const target = { id: cl.customer_id, name: cl.customer_name ?? 'this customer', version: cl.customer_version, message_by: cl.customer_message_by ?? null, phone: cl.customer_phone, email: cl.customer_email ?? null }
    const via = (way: string): string => (way === 'text' ? 'by text' : way === 'email' ? 'by email' : 'on WhatsApp')
    // From Roundly on WhatsApp (Stage 2): the words Meta approved, as the server will send them, shown here.
    const waJob = dueToday ? visitJobLabel(cl).toLowerCase() : jobWord?.toLowerCase() ?? null
    const waPlan = waPlanFor(cl, today, waJob)
    const message = paymentBreakdownMessage(cl, waPlan ? waPlan.words : dueToday
      ? paymentTodayMessage({ name: cl.customer_name, date: cl.date, amountPence: on > 0 ? toCollect(cl) : cl.amount_pence, job: visitJobLabel(cl).toLowerCase() })
      : chaseMessage({ name: cl.customer_name, date: cl.date, amountPence: on > 0 ? toCollect(cl) : cl.amount_pence, job: jobWord?.toLowerCase() ?? null }, rung))
    const subject = dueToday ? `Payment details from ${businessSettings().business_name}` : `Payment reminder from ${businessSettings().business_name}`
    const control = sendControl(target, reminderDrafts.get(`visit:${cl.id}`) ?? message, subject, {
      label: (way) => `${dueToday ? `Send ${via(way)}` : n === 0 ? `Chase ${via(way)}` : n === 1 ? 'Second chase' : 'Third chase'}${waPlan && !paymentBreakdownPrefix(cl) && way === 'whatsapp' ? ': send from Roundly' : ''}`,
      manualSend: true, className: `btn-whatsapp owed-chase${n > 0 ? ' owed-chase-again' : ''}`,
      changed: () => void load(),
      onConflict: () => { if (actionCurrent()) void load() },
    })
    const breakdown = owedBreakdown(cl, { onSaved: (fresh) => {
      const draft = reminderDrafts.get(`visit:${cl.id}`)
      // An empty box is an owner edit too. Only an absent/unchanged draft regenerates the payment words.
      if (draft !== undefined && draft !== message) {
        const prefix = paymentBreakdownPrefix(cl)
        reminderDrafts.set(`visit:${cl.id}`, paymentBreakdownPrefix(fresh) + (prefix && draft.startsWith(prefix) ? draft.slice(prefix.length) : draft))
      } else reminderDrafts.delete(`visit:${cl.id}`)
      void load()
    } })
    container.append(breakdown.node)
    control.root.addEventListener('click', breakdown.guard, true)
    control.root.addEventListener('auxclick', breakdown.guard, true)
    const link = control.link
    if (link) {
      const way = control.send.way
      const fromRoundly = !!waPlan && !paymentBreakdownPrefix(cl) && way === 'whatsapp'
      if (fromRoundly) {
        const serverSend = (e: MouseEvent): void => {
          const draft = reminderDrafts.get(`visit:${cl.id}`)
          if (draft !== undefined && draft !== message) return
          e.preventDefault();e.stopImmediatePropagation()
          if (e.type !== 'click' || e.button !== 0 || link.dataset.busy) return
          link.dataset.busy = '1';void sendFromRoundly(cl, message, waJob, link)
        }
        link.addEventListener('click',serverSend,true)
        link.addEventListener('auxclick',serverSend,true)
      }
      bindEmailSend(link, () => link.dataset.busy ? null : {
        href: link.getAttribute('href') ?? '', name: target.name,
        sent: () => { link.dataset.busy = '1'; countChase(via(way), link) },
      })
      if (fromRoundly) {
        // The other way, at hand: opens WhatsApp on the phone with these words and counts the chase, as before.
        const instead = el('a', { href: link.href, target: link.target, rel: 'noopener', class: 'link-button owed-wa-instead' }, 'Open WhatsApp instead') as HTMLAnchorElement
        // The words as they are in the box now, edited or not.
        instead.addEventListener('pointerdown', () => { instead.href = link.href })
        instead.addEventListener('focus', () => { instead.href = link.href })
        bindEmailSend(instead, () => ({ href: link.getAttribute('href') ?? '', name: target.name, sent: () => countChase(via(way), instead) }))
        queueMicrotask(() => control.root.after(instead))
      }
    }
    // Send and its ▾ at hand while the record is open (Liam, 25 Oct 2026: "the send text and dropdown"): their own row
    // straight after the message, held above the tab bar on a phone until you scroll down to it (owed.css .owed-send-pin).
    if (classic) actions.append(control.root)
    if (!classic) container.append(dueToday ? reminderPreview(`visit:${cl.id}`, message, control, target, subject, 'payment_today', 'Payment message to send')
      : reminderPreview(`visit:${cl.id}`, message, control, target, subject, rung === 'lean' ? 'chase_1' : rung === 'full' ? 'chase_2' : 'chase_3'),
      el('div', { class: 'owed-send-pin' }, control.root))
    // Chased some other way (a call, a text from the van): in sight under the send button, as Messages' "I messaged them
    // myself". Original keeps it under More.
    if (!classic) {
      const myself = el('button', { type: 'button', class: 'link-button owed-myself' }, dueToday ? 'I sent it myself' : 'I chased them myself')
      myself.addEventListener('click', () => countChase('by hand', myself))
      placeMyself(control.root, myself, actions)
    }
    if (!link && control.send.way === 'none' && tel) actions.append(el('a', { href: tel, class: 'owed-call-btn', 'aria-label': `Call ${who}` }, callLabel(first)))
  } else if (stage === 'call' && tel) {
    actions.append(debtCall(tel, first, `/api/cleans/${cl.id}`, cl.version))
  }

  // Paid: one control with two answers. Two separate dark "Paid cash" /
  // "Paid bank" buttons read as two unrelated decisions on every wrapped row.
  const pair = el('div', { class: 'owed-pair' })
  pair.append(el('span', { class: 'owed-pair-label' }, 'Paid'))
  for (const m of ['cash', 'bank'] as const) {
    const btn = el('button', { type: 'button', class: 'owed-pair-btn', 'aria-label': m === 'cash' ? 'Paid cash' : 'Paid bank' }, m === 'cash' ? 'Cash' : 'Bank')
    paidLook(btn, m)
    btn.addEventListener('click', () => payVisit(cl, m, container, btn, today))
    pair.append(btn)
  }
  actions.append(pair)

  // The other half of the decision, on unrecorded rows only. Until this
  // existed the only buttons on such a row said "paid", so a row where the
  // money never arrived could be marked settled or left alone — there was no
  // way to say "they haven't paid", which is what puts it into the chase list.
  if (cl.payment_status !== 'paid' && cl.payment_status !== 'unpaid') {
    const notPaid = el('button', { type: 'button', class: 'owed-not-paid' }, 'Not paid')
    notPaid.addEventListener('click', () => {
      notPaid.setAttribute('disabled', 'disabled')
      container.classList.add('cell-pending')
      void mutate({
        label: `Mark unpaid: ${cl.customer_name ?? ''} ${cl.date}`,
        method: 'PATCH',
        path: `/api/cleans/${cl.id}`,
        // chased 0: recording that money is owed is not a chase, and a stale
        // chase flag would hide the row from the follow-up it now needs.
        body: { version: cl.version, payment_status: 'unpaid', chased: 0 },
        onAck: () => { if (actionCurrent()) void load() },
        onConflict: () => { if (actionCurrent()) void load() },
      }).then((ack) => {
    if (!actionCurrent()) return
        if (!ack.ok && ack.status >= 400 && ack.status < 500) {
          notPaid.removeAttribute('disabled')
          container.classList.remove('cell-pending')
        }
        alert4xx(ack)
        undoPatch({ text: `Marked ${cl.customer_name ?? 'visit'} not paid`, label: `Undo not paid: ${cl.customer_name ?? ''} ${cl.date}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
          body: { payment_status: cl.payment_status, payment_method: cl.payment_method ?? null, chased: cl.chased }, after: () => void load() })
      })
    })
    actions.append(notPaid)
  }

  // ---- More -----------------------------------------------------------------
  const more = el('button', { type: 'button', class: 'owed-more', 'aria-haspopup': 'true', 'aria-expanded': 'false' }, 'More ▾')
  const menu = el('div', { class: 'owed-menu', role: 'menu' })
  // The property, not the attribute: a browser reflects one onto the other,
  // and the property is the half that reads back the same way it was set.
  menu.hidden = true
  const close = (): void => {
    menu.hidden = true
    more.setAttribute('aria-expanded', 'false')
    more.textContent = 'More ▾'
  }
  more.addEventListener('click', (e) => {
    e.stopPropagation()
    const wasOpen = openMenu?.menu === menu
    closeMenu()
    if (wasOpen) return
    menu.hidden = false
    more.setAttribute('aria-expanded', 'true')
    more.textContent = 'More ▴'
    openMenu = { menu, close }
    placeMenu(menu, more)
  })

  if (tel) {
    const call = el('a', { href: tel, class: 'owed-mi', role: 'menuitem' }, menuText(`Call ${first || who}`))
    call.addEventListener('click', () => closeMenu())
    menu.append(call)
  }
  // Chased some other way (a call, a text from the van): count it without
  // opening WhatsApp.
  if (stage === 'send' && classic) {
    const mark = el('button', { type: 'button', class: 'owed-mi', role: 'menuitem' }, menuText(n === 0 ? 'Mark chased' : `Mark chase ${n + 1} sent`, 'You chased another way, count it'))
    mark.addEventListener('click', () => { closeMenu(); countChase('by hand', mark) })
    menu.append(mark)
  }
  // Its opposite, and the one with no time limit on it: available for as long
  // as the row says chased, which deliberately includes the week the chase
  // button is hidden and the "call them" stage after three — being stuck is
  // exactly when this is needed. Clearing the date always brings the button
  // back, so the second line is true at every rung.
  // Not for a reminder Roundly texted itself (Send all, 0093): a sent text can't be taken back.
  if (stage !== null && n >= 1 && !(cl as Clean & { wa_attempt?: string | null }).wa_attempt) {
    const notSent = el('button', { type: 'button', class: 'owed-mi', role: 'menuitem' }, menuText("I didn't send that chase", 'Puts the Chase button back'))
    notSent.addEventListener('click', () => { closeMenu(); uncountChase(notSent) })
    menu.append(notSent)
  }
  const open = el('a', { href: `#/customer/${cl.customer_id}`, class: 'owed-mi', role: 'menuitem' }, menuText(`Open ${first || who}'s page`))
  open.addEventListener('click', () => closeMenu())
  menu.append(open)
  // The same visit on the Diary, on its day and picked out, as Messages' "Open diary" does (docs/DIARY-AND-MESSAGES.md,
  // Owed). New only: Original keeps the 5 Oct menu.
  if (!classic) {
    const diary = el('a', { href: '#/schedule', class: 'owed-mi owed-mi-diary', role: 'menuitem' }, menuText('Open diary', `${first || who}'s ${shortDate(cl.date)} visit, on its day`))
    diary.addEventListener('click', (e) => {
      e.preventDefault(); closeMenu()
      void import('./schedule').then(({ focusDiary }) => { focusDiary([cl.id], cl.date, null, false); window.location.hash = '#/schedule' })
    })
    menu.append(diary)
  }
  menu.append(el('div', { class: 'owed-menu-sep' }))
  const clears = cl.amount_pence === null || cl.amount_pence === undefined ? 'Clears the money owed' : `Clears the ${money(cl.amount_pence)}`
  const notDone = el('button', { type: 'button', class: 'owed-mi owed-mi-danger', role: 'menuitem' }, menuText("This visit wasn't done", `${clears} and ${whereTheyGo(cl)}`))
  notDone.addEventListener('click', () => { closeMenu(); markNotDone(cl, notDone, container) })
  menu.append(notDone)

  actions.append(more, menu)
  if (tel && !classic && !actions.querySelector('.owed-call-btn')) actions.append(el('a', { href: tel, class: 'owed-call-btn', 'aria-label': `Call ${who}` }, callLabel(first)))
  if (!classic) pairUp(actions)
  container.append(actions)
}

// New's buttons sit two to a row like a Messages card's: the send button, "I chased them myself", Paid and Not paid take
// a whole row; Call and More share one, and a button left on its own takes the whole row.
function pairUp(actions: HTMLElement): void {
  const halves = Array.from(actions.children).filter((n) => (n.classList.contains('owed-call-btn') && !n.classList.contains('owed-call-main')) || n.classList.contains('owed-more'))
  if (halves.length % 2) halves[halves.length - 1].classList.add('owed-whole')
}

// Paid by cash or by bank: the same save from the card's "Paid" and from the one-tap "Paid" on a phone row (Liam, 8 Oct
// 2026: "with one tap on mobile i could mark it"). host is what goes green and slides away: the card, or the row.
function payVisit(cl: Clean, m: 'cash' | 'bank', host: HTMLElement, btn: HTMLElement, today: string, from: HTMLElement = btn): void {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const actionCurrent = (): boolean => onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition
  const who = cl.customer_name ?? `#${cl.customer_id}`
  // On a round with brought money (0043) cash asks what was taken, starting on what is still to pay, and every
  // payment carries the day it was paid.
  const moneyRound = !!payload?.brought
  const pay = (taken: number | null): void => {
    btn.setAttribute('disabled', 'disabled')
    // Green at once; once the server has it the row slides away and the
    // total counts down to the new figure (Liam, 2026-09-24).
    host.classList.add('cell-pending')
    // The paid moment (paid-moment.ts): green from the tap, a tick, the buzz; taken back below if the save is refused.
    const takeBack = paidMoment({ host, from, way: m, line: host.querySelector<HTMLElement>('.owed-line'), mark: host.querySelector<HTMLElement>('.owed-avatar') })
    const amountEl = host.querySelector<HTMLElement>('.owed-queue-money') ?? from
    void mutate({
      label: `Mark paid: ${cl.customer_name ?? ''} ${cl.date}`,
      method: 'PATCH',
      path: `/api/cleans/${cl.id}`,
      body: { version: cl.version, payment_status: 'paid', payment_method: m, chased: 0, ...(moneyRound ? { paid_on: today } : {}), ...(taken !== null ? { taken_pence: taken } : {}) },
      onAck: (ack) => {
        if (!actionCurrent()) return
        countFromPence = combinedTotal()
        const paid = (ack.row as Clean | undefined)?.payment_status === 'paid'
        // What came in flies into Paid today: the cash taken on a money round, else the visit's figure.
        const received = typeof ack.money?.receipt_pence === 'number' ? ack.money.receipt_pence : taken ?? toCollect(cl) ?? cl.amount_pence ?? 0
        if (received > 0) flyToPaidToday(amountEl, received)
        if (paid) { justPaidAt = Date.now(); host.classList.add('owed-paying'); const gen = routeGen; void slideAway(host, 380).then(() => { if (gen === routeGen) void load() }) }
        else void load()
      },
      onConflict: () => { if (actionCurrent()) void load() },
    }).then((ack) => {
  if (!actionCurrent()) return
      if (!ack.ok && ack.status >= 400 && ack.status < 500) {
        btn.removeAttribute('disabled')
        host.classList.remove('cell-pending', 'owed-paying')
        takeBack()
      }
      if (!ack.ok && m === 'cash' && moneyRound && taken !== null) askCashTaken(host, taken, pay)
      alert4xx(ack)
      const left = ack.money?.to_pay_pence
      const received = ack.money?.receipt_pence
      const settled = (ack.row as Clean | undefined)?.payment_status === 'paid'
      const text = received === 0 ? `No cash recorded from ${who}${typeof left === 'number' ? ` · ${money(left)} still to pay` : ''}`
        : !settled && typeof received === 'number' ? `Received ${money(received)} from ${who}${typeof left === 'number' ? ` · ${money(left)} still to pay` : ''}` : `Marked ${who} paid by ${m}`
      const thanks = settled || ack.status === 0 ? thanksLink(cl) : undefined
      if (moneyRound) paymentUndo(ack, who, cl.id, text, thanks)
      else undoPatch({ text, label: `Undo paid: ${who}`, path: `/api/cleans/${cl.id}`, ack, version: cl.version,
        body: { payment_status: cl.payment_status, payment_method: cl.payment_method ?? null, chased: cl.chased }, after: () => void load(), link: thanks })
    })
  }
  if (moneyRound && m === 'cash') askCashTaken(host, toCollect(cl) ?? 0, pay)
  else pay(null)
}

// The second half of the "wasn't done" explanation, kept honest about the
// only-clean case: a customer with no other done clean has no last clean for
// Messages to work from, so they land on the diary instead.
function whereTheyGo(cl: Clean): string {
  const other = hasAnotherDoneClean(cl)
  if (other === true) return 'puts them in Messages'
  if (other === false) return 'puts them under Past bookings on the diary'
  return 'puts the visit back to booked'
}

// ---- the screen ------------------------------------------------------------

// line: the row's third line, like a Messages row's. For a visit it is the shared words for that visit (visit-words.ts,
// the same words its Diary row shows): "visit 6 Oct · Done · chased · not paid".
// pay: the one-tap Paid a phone row carries (visits, and accounts with something left), the same save as the card's.
// markDone: a visit of today's nobody has marked done ("Done today?") opens the usual "Log completed work".
interface QueueRecord { key: string; name: string; address: string; date: string; amount: number | null; stage: string; render: (card: HTMLElement) => void; paths: string[]; context: string; line: string; moneyLabel?: string; pay?: (m: 'cash' | 'bank', host: HTMLElement, btn: HTMLElement, from?: HTMLElement) => void; markDone?: () => void; notDone?: () => void; pill?: [string, string]; invoice?: { label: string; run: () => void }; remind?: () => void; business?: HintFacts; held?: { since: { id: number; at: string; words: string } | null; paused: boolean; customerId: number } }
// The branded dropdown's button for one of the toolbar's two choices, to give focus back after a redraw.
function triggerFor(root: HTMLElement, label: string): HTMLElement | undefined {
  return Array.from(root.querySelectorAll<HTMLElement>('.roundly-select-trigger')).find((t) => t.getAttribute('aria-label') === label)
}
// Group headings as Messages draws its stages: a dot, the name, then how many and how much on the right.
const OWED_GROUPS: [string, string][] = [['open', 'Done?'], ['invoice', 'Invoice to send'], ['today', 'Payment message to send'], ['send', 'To chase'], ['waiting', 'Waiting'], ['call', 'Call'], ['unrecorded', 'Payment not recorded'], ['cash', 'Cash to confirm']]
// A visit of today's that should be finished by now (Owed's "Done today?", Liam 8 Oct 2026): its time has passed, or it
// has no time and it is 5pm. now is "HH:MM" on this phone.
export function shouldBeDone(timeSlot: string | null | undefined, now: string): boolean {
  const at = /(\d{1,2}):(\d{2})/.exec(timeSlot ?? '')
  return at ? now >= `${at[1].padStart(2, '0')}:${at[2]}` : now >= '17:00'
}
const clockNow = (): string => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` }

// The full "Log completed work" form, for a note, a photo or a different price; the pop-up below links to it.
function openFullDone(cl: Clean, refresh: () => void, onSaved?: () => void): void {
  void import('../booking').then(({ openBooking }) => openBooking({ customerId: cl.customer_id, cleanId: cl.id, completed: true, onCompleted: () => { onSaved?.(); refresh() } }))
}

// The screen showing now reads its records again: Owed itself, or any other screen through the app's data-changed
// signal (the Diary, the customer page).
function refreshAfterDone(): void {
  if (onOwed()) void load(); else window.dispatchEvent(new CustomEvent('dropby:data-changed'))
}

// The same pop-up from any screen with a visit to mark done (Liam, 9 Oct 2026: Done on the Diary "keeps the old method
// which is not particularly helpful unlike the new one we shipped": one way to mark a visit done everywhere). The
// customer's latest facts are read first (how they are messaged, for the payment message); with no signal it opens on
// the visit as the screen has it. onSaved: the visit was marked done (the Diary's tick motion).
export async function openDonePopup(cl: Clean, opts: DoneOpts = {}): Promise<void> {
  const read = await withCustomerFacts(cl)
  // Taken off the round (the read answers 404) is not no signal (tidy-up item 2, AGREED section 2): nothing to mark.
  if (read === 'gone') { toast(`${cl.customer_name ?? 'That customer'} was taken off the round.`); refreshAfterDone(); return }
  // The visit's own row as read just now, then their page's facts (no signal: as this screen has it).
  const visit: Clean = { ...cl, ...(read.row ?? {}), ...read.facts }
  if (visit.state !== 'booked') { toast(`${visit.customer_name ?? 'That visit'} is already marked ${visit.state === 'done' ? 'done' : visit.state}.`); refreshAfterDone(); return }
  openDone(visit, opts)
}

// The visit with its customer's facts as their page has them now (how they are messaged, for the payment message;
// Commercial), and the visit's own row as read. 'gone': taken off the round. No answer: nothing read (read false).
async function withCustomerFacts(cl: Clean): Promise<{ facts: Partial<Clean>; row: Clean | null; read: boolean } | 'gone'> {
  try {
    const detail = await api<{ customer: Customer; cleans: Clean[] }>(`/api/customers/${cl.customer_id}`)
    const c = detail.customer
    return { read: true, row: detail.cleans.find((v) => v.id === cl.id) ?? null, facts: { customer_name: c.name,
      customer_address: addressLine(c.address, c.postcode, ', ') || cl.customer_address,
      customer_phone: c.phone ?? cl.customer_phone, customer_message_by: c.message_by ?? null, customer_email: c.email ?? null, customer_version: c.version,
      // Commercial (0080): whether the invoice follows Not paid yet, from their page as it is now.
      ...({ customer_commercial: (c as Customer & { commercial?: number }).commercial ?? 0, customer_invoice_mode: (c as Customer & { invoice_mode?: string | null }).invoice_mode ?? null } as object) } }
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return 'gone'
    return { facts: {}, row: null, read: false }
  }
}

// Job done saved not paid on the owner's phone (Liam, 21 Oct 2026, picture A: "only when its not a worker ... if you own
// the round and are working it"): the payment message comes straight up, as after Not paid yet in the pop-up below,
// from its step 2 (the saved row and money answer, credit, a clean older than a week, Not now, Put it back, the Done's
// Undo once it closes). `cl` is the visit as it was before the save. `stillOn`: the Job done's screen, account and
// Undo bar are still the ones it saved from (Astra: an Undo tapped, or a screen left, while this waited opens nothing).
// Only when their page has been read and the visit is still done and not paid: a commercial customer invoiced each
// visit, one taken off the round, or no answer (whether they are commercial is unknown: Astra) get `otherwise` (the Job
// done's own Send invoice beside its Undo).
export async function openPaymentAfterDone(cl: Clean, saved: Ack, stillOn: () => boolean, otherwise: () => void): Promise<void> {
  const read = await withCustomerFacts(cl)
  if (!stillOn()) return
  if (read === 'gone' || !read.read || read.row?.state !== 'done' || read.row.payment_status !== 'unpaid') { otherwise(); return }
  const visit: Clean = { ...cl, ...read.facts }
  const priced = (read.row.amount_pence ?? 0) > 0 && (read.row as Clean & { carryover?: number | null }).carryover !== 1
  if (priced && invoiceAfterEachJob(commercialFacts(visit))) { otherwise(); return }
  openDone(visit, { saved })
}

// "Done today?" Done: one pop-up, like Messages' and the Diary's "Ask about a day" (Liam, 8 Oct 2026 evening: "a done
// that opens a different flow, like with the messages ... and then it will move into a flow of ... sending the payment
// message"). How was it paid: Paid cash / Paid by bank mark it done and paid, Undo after; Not paid yet marks it done
// and shows the payment message (Payment due today) in the same pop-up, ready to send. The saves are the ones the
// Done form and Owed's chase already make; nothing new is stored.
let doneDialog: HTMLDialogElement | null = null
// saved: already marked done, not paid (openPaymentAfterDone): the pop-up opens on its step 2.
interface DoneOpts { onSaved?: () => void; saved?: Ack }
function openDone(cl: Clean, opts: DoneOpts = {}): void {
  doneDialog?.close()
  const previous = document.activeElement as HTMLElement | null
  const generation = routeGen, partition = queuePartition()
  const current = (): boolean => routeGen === generation && queuePartition() === partition
  const today = todayISO()
  const name = cl.customer_name ?? 'this customer'
  const first = firstName(cl.customer_name) || name
  const path = `/api/cleans/${cl.id}`
  const dialog = el('dialog', { class: 'booking-dialog owed-done-dialog', 'aria-labelledby': 'owed-done-title' }) as HTMLDialogElement
  doneDialog = dialog
  const title = el('h2', { id: 'owed-done-title' }, `Mark ${first} done`)
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const content = el('div', { class: 'booking-content owed-done-content' })
  dialog.append(el('header', { class: 'booking-head' }, title, close), content)
  close.addEventListener('click', () => dialog.close())
  // Its words tapped open with the keyboard up: the pop-up stands above the keyboard, Send in sight (Sonnet and GPT-6.1).
  const freeKeyboard = keepAboveKeyboard(dialog)
  dialog.addEventListener('close', () => { freeKeyboard(); dialog.remove(); if (doneDialog === dialog) doneDialog = null; if (current()) refreshAfterDone(); if (previous?.isConnected) previous.focus({ preventScroll: true }) })
  const who = el('p', { class: 'muted' }, [name, tidyAddress(cl.customer_address)].filter(Boolean).join(' · '))
  // A visit from another day (the Diary logs a missed day too) says its day; today's says Today.
  const isToday = cl.date === today
  const visitLine = `${isToday ? 'Today' : dayLabel(cl.date)}${shownVisitTime(cl) ? `, ${shownVisitTime(cl)}` : ''}${cl.amount_pence != null ? ` · ${money(cl.amount_pence)}` : ''}${cl.worker ? ` · ${cl.worker}` : ''}`
  // A commercial customer invoiced each visit, on an owner's phone with Invoices on (0080; Liam, 20 Oct 2026;
  // docs/plans/commercial/AGREED.md 2, 3): Not paid yet is followed by their invoice, not the payment message. Their page
  // as read just now, else as this phone has it, with a switch still waiting for signal laid over (AGREED.md 15). Once
  // a month keeps today's flow exactly.
  // A visit with no price (or £0) has nothing to invoice: today's Not paid yet, with its price on Owed (the recheck's G6).
  const priced = (cl.amount_pence ?? 0) > 0 && (cl as Clean & { carryover?: number | null }).carryover !== 1
  const invoiceAfter = priced && invoiceAfterEachJob(commercialFacts(cl))
  // Back to booked: the Done form's own Undo (booking.ts), exactly as the visit was.
  const doneBack = { state: 'booked', confirmed: cl.confirmed ?? 0, time_agreed_for: cl.time_agreed_for ?? null, note: cl.note ?? null }
  const doneUndo = (ack: Ack, text: string): void => undoPatch({ text, label: `Undo done: ${name} ${cl.date}`, path, ack, version: cl.version,
    body: doneBack, after: refreshAfterDone })
  // Marked done, not paid, and the payment message not counted yet: the Done's Undo is offered once the pop-up closes.
  let doneSaved: { ack: Ack; text: string } | null = null
  dialog.addEventListener('close', () => { if (doneSaved) doneUndo(doneSaved.ack, doneSaved.text); doneSaved = null })
  // Leaving for another page ("Change wording", Back) closes the pop-up instead of leaving it over that page; a visit
  // already marked done says where its message waits (bug hunt 8 Oct 2026, owed-today #4).
  const leave = (): void => {
    if (!dialog.open) return
    const saved = doneSaved
    dialog.close()
    if (saved) toast(`${first} is marked done, not paid. ${paymentMessageDue(cl.date, today) ? 'The payment message waits in Owed under "Payment message to send".' : 'Their reminder is ready in Owed under "To chase".'}`)
  }
  window.addEventListener('hashchange', leave)
  dialog.addEventListener('close', () => window.removeEventListener('hashchange', leave))

  // Step 1: how was it paid?
  clear(content)
  content.append(who, el('p', { class: 'owed-done-visit' }, visitLine), el('h3', { class: 'owed-done-ask' }, 'How was it paid?'))
  const choices = el('div', { class: 'owed-done-choices', role: 'group', 'aria-label': `How ${name} paid` })
  const error = el('p', { class: 'form-error', role: 'alert' })
  const moneyRound = getMe()?.broughtMoney === true || !!payload?.brought
  const setBusy = (on: boolean): void => { for (const b of choices.querySelectorAll('button')) b.disabled = on }
  const save = async (status: 'paid' | 'unpaid', method: 'cash' | 'bank' | null, taken: number | null, pressed?: HTMLElement): Promise<void> => {
    setBusy(true); error.textContent = ''
    // Paid: the paid moment on the tap (paid-moment.ts), taken back if the save is refused. Not paid yet: its own,
    // orange from the button (tidy-up item 2; Liam, 20 Oct 2026), taken back the same way.
    const takeBack = status === 'paid' && method ? paidMoment({ host: choices, from: pressed ?? choices, way: method })
      : status === 'unpaid' ? owedMoment({ host: choices, from: pressed ?? choices }) : () => {}
    let queuedKey: string | null = null
    const ack = await mutate({ label: `Log: ${name} ${cl.date}`, method: 'PATCH', path,
      body: { version: cl.version, state: 'done', payment_status: status, payment_method: method, ...(moneyRound && method ? { paid_on: today } : {}), ...(taken !== null ? { taken_pence: taken } : {}) },
      onQueued: (m) => { queuedKey = m.key } })
    if (!dialog.isConnected || !current()) return
    if (!ack.ok && ack.status >= 400) {
      setBusy(false)
      takeBack()
      // A customer taken off the round meanwhile, on a round with money brought from Squeegee: the server refuses it
      // (functions/api/cleans/[id].ts customerGone); said in their name (Sol's edge cases G E01, 20 Oct 2026).
      error.textContent = ack.status === 409 ? 'This visit was changed somewhere else. Close this and open it again.'
        : ack.code === 'customer_gone' ? `${name} was taken off the round.` : ack.error ?? "That didn't save."
      return
    }
    // Saved, or kept on this phone until there is signal (the queue sends it, then anything after it).
    const kept = !ack.ok
    opts.onSaved?.()
    if (status === 'paid') {
      // A moment to see it go green, then the pop-up closes.
      if (!reducedMotion()) await new Promise((r) => window.setTimeout(r, 650))
      if (dialog.isConnected) dialog.close()
      // What the save says happened, as the card's own Cash says it (owed-today #5): on a round with money brought from
      // Squeegee, part of the price taken leaves the visit done with the rest still to pay.
      const left = ack.money?.to_pay_pence, received = ack.money?.receipt_pence
      const settled = (ack.row as Clean | undefined)?.payment_status === 'paid'
      const still = typeof left === 'number' ? ` · ${money(left)} still to pay` : ''
      doneUndo(ack, kept ? `Marked ${name} done, paid by ${method} · saves when there is signal`
        : received === 0 ? `Marked ${name} done. No cash recorded${still}`
        : !settled && typeof received === 'number' ? `Marked ${name} done. Received ${money(received)}${still}`
        : `Marked ${name} done, paid by ${method}`)
      return
    }
    // Its Undo waits until the pop-up closes (it sat hidden behind it, then the message's Undo replaced it: bug hunt
    // 8 Oct 2026, owed-today #3); inside the pop-up, "Not done after all?" takes it back.
    doneSaved = { ack, text: `Marked ${name} done, not paid yet` }
    // Waiting for signal: said so, and nothing about Owed or the payment message until the server has answered (credit
    // can change before it lands: tidy-up AGREED section 2, Sol's edge cases G E02).
    if (kept) { waitForSignal(queuedKey); return }
    void answered(ack.row as Clean | undefined, { notice: ack.money ?? null })
  }

  // Not paid yet, as the server answered it (tidy-up item 2, AGREED section 2): the banner, the payment message and its
  // amount from the saved row and what the save did with money, never the visit as this pop-up opened (G E02); a save
  // that lands after the pop-up was closed shows nothing late (S E15: the Owed count's pop is the only sign).
  // fromAck: what the save's own answer said with money (from the queue, when its landing carries it; else it is read).
  const answered = async (row: Clean | undefined, fromAck: { notice: MoneyNotice | null } | null): Promise<void> => {
    if (!dialog.isConnected || !current()) return
    // Still on the round? A customer taken off it after this opened: the visit saved done, but Owed lists no removed
    // customer (functions/api/owed.ts), so it is not said to be there (G E01). No signal for this read: still there.
    let fresh: Clean | null = null, gone = false
    // Their page as it is now too: a Commercial switch refused while this waited for signal is not followed (the
    // recheck's G5); with no read, as this phone has it, waiting changes laid over.
    let now: { id: number; commercial?: number | null; invoice_mode?: string | null } | null = null
    // Looks like a business: their page as read just now, never this phone's older copy (no read, no question).
    let seen: (HintCustomer & { version: number }) | null = null
    try {
      const detail = await api<{ cleans: Clean[]; customer?: Customer & { commercial?: number | null; invoice_mode?: string | null; business_hint_dismissed?: number | null } }>(`/api/customers/${cl.customer_id}`)
      fresh = detail.cleans.find((v) => v.id === cl.id) ?? null
      if (detail.customer) {
        now = { id: cl.customer_id, commercial: detail.customer.commercial ?? 0, invoice_mode: detail.customer.invoice_mode ?? null }
        seen = { ...now, version: detail.customer.version, name: detail.customer.name, address: detail.customer.address ?? null, business_hint_dismissed: detail.customer.business_hint_dismissed ?? 0 }
      }
    } catch (err) { gone = err instanceof ApiError && err.status === 404 }
    const savedAmount = row?.amount_pence ?? fresh?.amount_pence ?? cl.amount_pence ?? null
    const invoiceNow = (savedAmount ?? 0) > 0 && invoiceAfterEachJob(now ?? commercialFacts(cl))
    if (!dialog.isConnected || !current()) return
    if (gone) { showGone(); return }
    // Credit covered it: the server saved it paid (functions/lib/money.ts), so it closes like Paid. On a round with money
    // brought from Squeegee the read just after counts too (an answer sent again carries the first save's row only).
    if (row?.payment_status === 'paid' || (moneyRound ? fresh?.payment_status === 'paid' : !row && fresh?.payment_status === 'paid')) {
      const saved = doneSaved
      doneSaved = null
      if (!reducedMotion()) await new Promise((r) => window.setTimeout(r, 650))
      if (dialog.isConnected) dialog.close()
      if (saved) doneUndo(saved.ack, `Marked ${name} done, paid from their credit`)
      return
    }
    // What is still to pay. Without money brought from Squeegee there is no credit: the price. With it: the save's own
    // figure when its answer gave one, else the visit as read just now; an answer that says nothing of its money (from
    // the queue, or the save sent again after its first answer was lost) never means the whole price (Astra's final
    // review, finding 1, 20 Oct 2026). With neither, credit may have gone on it, so no amount is said and no payment
    // message offered: Owed has both (GPT-6.1's review of the build, E01: it asked for £25 when £20 was left).
    const amount = row?.amount_pence ?? cl.amount_pence ?? null
    const toPay = !moneyRound || amount === null ? amount : fromAck?.notice?.to_pay_pence ?? (fresh ? toCollect(fresh) : null)
    const known = !moneyRound || amount === null || toPay !== null
    const pence = toPay
    const added = `Added to Owed${pence != null ? ` · ${money(pence)}` : ''}`
    // Commercial: their invoice, whatever the visit's age; the server works out what is still to pay on it.
    if (invoiceNow) { showInvoiceStep(added); return }
    const toMessage = (): void => {
      // The payment message is for a clean of the last week; an older one's goes to Owed's reminders.
      if (!paymentMessageDue(cl.date, today)) { dialog.close(); toast(`${added}. ${first}'s reminder is ready in Owed under "To chase".`); return }
      if (!known) {
        clear(content)
        title.textContent = `Marked ${first} done`
        content.append(owedBanner(added, `${first} owes you. Owed will remind you.`), who,
          el('p', { class: 'owed-done-visit' }, `${isToday ? 'Done today' : `Done ${dayLabel(cl.date)}`} · not paid yet`),
          el('p', { class: 'owed-help' }, 'Their payment message is ready in Owed under "Payment message to send", with what is still to pay.'), notDoneButton())
        return
      }
      showMessage({ ...cl, ...(row ?? {}), state: 'done', payment_status: 'unpaid', payment_method: null, version: typeof row?.version === 'number' ? row.version : cl.version + 1 }, pence, added)
    }
    // Looks like a business (design B; Astra 9): asked here, before any payment message goes, so a business never gets
    // one first. Only with something to pay and the customer read just now.
    const reason = (pence ?? savedAmount ?? 0) > 0 && known && seen ? businessHint(seen) : null
    if (reason && seen) { showBusinessStep(reason, seen, pence, added, toMessage); return }
    toMessage()
  }

  // Waiting for signal (tidy-up item 2): said plainly; when the save lands with the pop-up still open, it goes on as if
  // it had saved at once; refused when it is sent, it says so (the saving indicator keeps it, as for any save).
  const waitForSignal = (key: string | null): void => {
    clear(content)
    content.append(owedBanner('Waiting for signal', `${first} goes into Owed as soon as this saves.`), who,
      el('p', { class: 'owed-done-visit' }, `${isToday ? 'Done today' : `Done ${dayLabel(cl.date)}`} · not paid yet`), error, notDoneButton())
    content.querySelector('.owed-added')?.classList.add('owed-added-waiting')
    if (!key) return
    const landed = (e: Event): void => {
      const d = (e as CustomEvent<{ key?: string; row?: unknown; money?: MoneyNotice | null }>).detail
      if (d?.key !== key) return
      stop()
      // The server's own money answer, when the landing carries it (E01).
      void answered(d.row as Clean | undefined, d && 'money' in d ? { notice: d.money ?? null } : null)
    }
    const refused = (): void => {
      const q = getQueue().find((x) => x.key === key)
      if (!q || (q.status !== 'failed' && q.status !== 'conflict')) return
      stop()
      error.textContent = `That didn't save: ${q.status === 'conflict' ? 'this visit was changed somewhere else' : q.error ?? 'it was refused'}. It is in the saving indicator at the bottom of the screen.`
    }
    const stop = (): void => { window.removeEventListener('roundly:queued-landed', landed); window.removeEventListener('dropby:data-changed', refused) }
    window.addEventListener('roundly:queued-landed', landed)
    window.addEventListener('dropby:data-changed', refused)
    dialog.addEventListener('close', stop)
  }

  // Taken off the round after the pop-up opened (G E01): the visit is done; Owed does not list them.
  const showGone = (): void => {
    clear(content)
    title.textContent = `Marked ${first} done`
    content.append(who, el('p', { class: 'owed-done-gone' }, `Marked done. ${name} was taken off the round, so this isn't in Owed.`))
  }
  const pick = (label: string, cls: string, act: (b: HTMLButtonElement) => void): void => {
    const b = el('button', { type: 'button', class: `owed-done-choice ${cls}` }, label)
    b.addEventListener('click', () => act(b))
    choices.append(b)
  }
  // On a round that tracks cash amounts, Cash asks what was taken, starting on the price, as everywhere else.
  pick('Paid cash', 'owed-done-cash', (b) => { if (moneyRound) askCashTaken(content, toCollect(cl) ?? cl.amount_pence ?? 0, (pence) => void save('paid', 'cash', pence, b)); else void save('paid', 'cash', null, b) })
  pick('Paid by bank', 'owed-done-bank', (b) => void save('paid', 'bank', null, b))
  // Cash and Bank in their own colours, as everywhere a payment is marked.
  for (const [cls, way] of [['owed-done-cash', 'cash'], ['owed-done-bank', 'bank']] as const) { const b = choices.querySelector<HTMLElement>(`.${cls}`); if (b) paidLook(b, way) }
  pick(invoiceAfter ? 'Not paid yet: send invoice' : 'Not paid yet', 'owed-done-unpaid', (b) => void save('unpaid', null, null, b))
  // Not paid yet's own look: orange with a clock (tidy-up item 2; Liam, 20 Oct 2026).
  const unpaidChoice = choices.querySelector<HTMLElement>('.owed-done-unpaid')
  if (unpaidChoice) owedLook(unpaidChoice)
  const full = el('button', { type: 'button', class: 'link-button owed-done-full' }, 'Add a note or photo instead (the full form)')
  full.addEventListener('click', () => { dialog.close(); openFullDone(cl, refreshAfterDone, opts.onSaved) })
  content.append(choices, error, full)

  // Looks like a business, after Not paid yet (Liam, 26 Oct 2026, design B; Astra's check): Send invoice switches
  // Commercial on and, once that has saved, shows their invoice here; Payment message goes on as before; Not a business
  // and Keep payment messages stop the question for them and go on as before too.
  const showBusinessStep = (reason: string, c: HintCustomer & { version: number }, pence: number | null, added: string, next: () => void): void => {
    clear(content)
    title.textContent = `How do you want to ask ${first}?`
    const customerPath = `/api/customers/${cl.customer_id}`
    const err = el('p', { class: 'form-error', role: 'alert' })
    const tile = (drawing: Drawing, head: string, line: string, cls: string): HTMLButtonElement =>
      el('button', { type: 'button', class: `owed-done-business-tile ${cls}` }, el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 19)),
        el('span', { class: 'owed-tile-words' }, el('strong', {}, head), el('span', {}, line))) as HTMLButtonElement
    const invoice = tile('invoiceAdd', 'Send invoice', 'Now and after each job', 'owed-done-business-invoice')
    const message = tile('bubble', 'Payment message', 'Like everyone else', 'owed-done-business-message')
    const skip = el('div', { class: 'owed-business-skip' })
    const area = el('div', { class: 'owed-business-area owed-done-business' }, el('div', { class: 'owed-done-business-tiles' }, invoice, message), skip)
    const busy = (on: boolean): void => { for (const b of area.querySelectorAll('button')) (b as HTMLButtonElement).disabled = on }
    // Undo inside the pop-up (Astra 6): back to the payment message, Commercial off again.
    const undoButton = (ack: Ack): HTMLButtonElement => {
      const undo = el('button', { type: 'button', class: 'link-button owed-business-undo' }, 'Not a business after all? Keep payment messages') as HTMLButtonElement
      undo.addEventListener('click', () => {
        undo.setAttribute('disabled', 'disabled')
        const version = typeof (ack.row as { version?: number } | undefined)?.version === 'number' ? (ack.row as { version: number }).version : c.version + 1
        void mutate({ label: `Undo commercial: ${name}`, method: 'PATCH', path: customerPath, body: { version, commercial: 0 } }).then((back) => {
          if (!dialog.isConnected || !current()) return
          if (refusedAck(back)) { undo.removeAttribute('disabled'); toast(back.error ?? "That didn't save. Try again."); return }
          next()
        })
      })
      return undo
    }
    invoice.addEventListener('click', () => {
      busy(true); err.textContent = ''
      void mutate({ label: `Commercial: ${name}`, method: 'PATCH', path: customerPath, body: { version: c.version, commercial: 1 } }).then((ack) => {
        if (!dialog.isConnected || !current()) return
        if (refusedAck(ack)) {
          busy(false)
          err.textContent = ack.status === 409 ? `${name}'s details were changed somewhere else. Close this and send it from Owed.` : ack.error ?? "That didn't save. Try again."
          return
        }
        // Kept on this phone (no signal, or a server error it retries: Sol 3, 4): the invoice needs the server, so it waits
        // in Owed; the pop-up stays with this switch's own Undo, not Done's.
        if (!ack.ok) {
          clear(content)
          title.textContent = `${first} is a business now`
          content.append(owedBanner(added, `Saved on this phone. ${first}'s invoice waits in Owed under "Invoice to send" once it sends.`), who, undoButton(ack))
          return
        }
        showInvoiceStep(added, undoButton(ack))
      })
    })
    message.addEventListener('click', () => next())
    for (const label of ['Not a business', 'Keep payment messages']) {
      const b = el('button', { type: 'button', class: 'link-button' }, label)
      b.addEventListener('click', () => {
        busy(true)
        void mutate({ label: `Don't ask if business: ${name}`, method: 'PATCH', path: customerPath, body: { version: c.version, business_hint_dismissed: 1 } }).then((ack) => {
          if (refusedAck(ack)) toast(`Roundly may ask about ${name} again: ${ack.error ?? "that didn't save"}.`)
        })
        next()
      })
      skip.append(b)
    }
    content.append(owedBanner(added, `${first} owes you.`), who,
      el('p', { class: 'owed-business-line' }, el('strong', {}, 'Looks like a business'), ` (it says ${reason}). How do you want to ask for ${pence != null ? money(pence) : 'it'}?`),
      area, err, notDoneButton())
  }

  // Step 2 for a commercial customer (AGREED.md 3): "Added to Owed · £X", then the invoice for that day's jobs marked Not
  // paid yet, chosen by the server (never this pop-up's stand-in row), their way first; Not now keeps it in Owed under
  // "Invoice to send"; "Not done after all? Put it back" only until the invoice is made (AGREED.md 6).
  const showInvoiceStep = (added: string, undoBusiness?: HTMLElement): void => {
    clear(content)
    title.textContent = `Send ${first} the invoice`
    const host = el('div')
    let banner = owedBanner(added, `${first} owes you. The invoice asks them for it.`)
    const back = notDoneButton()
    const later = el('button', { type: 'button', class: 'btn-secondary' }, 'Not now')
    later.addEventListener('click', () => dialog.close())
    content.append(banner, who,
      el('p', { class: 'owed-done-visit' }, `${isToday ? 'Done today' : `Done ${dayLabel(cl.date)}`} · not paid yet`), host,
      el('div', { class: 'owed-done-row' }, later), el('p', { class: 'owed-help' }, 'Not now keeps them in Owed under "Invoice to send".'), ...(undoBusiness ? [undoBusiness] : []), back)
    // A tap on any way to send it starts making the invoice: its Undo goes then, never racing the invoice (Sol 1).
    if (undoBusiness) host.addEventListener('click', (e) => { if (!(e.target as Element | null)?.closest?.('[data-invoice-breakdown]') && (e.target as Element | null)?.closest?.('button, a')) undoBusiness.remove() }, true)
    renderInvoiceStep(host, cl.customer_id, { dayOf: cl.id }, {
      onVisitSaved: (visit) => {
        if (visit.id === cl.id && doneSaved?.ack.row) doneSaved.ack.row = { ...doneSaved.ack.row, version: visit.version }
      },
      onBreakdownSaved: (owed) => { const fresh = owedBanner(`Added to Owed · ${money(owed)}`, `${first} owes you. The invoice asks them for it.`); banner.replaceWith(fresh); banner = fresh },
      close: () => { if (dialog.isConnected) dialog.close() },
      // Made: Done can no longer be put back from here (the invoice holds it), and no Undo of Done is offered after.
      onMade: () => { doneSaved = null; back.remove(); undoBusiness?.remove() },
    })
  }

  // Step 2, after Not paid yet: "Added to Owed · £X" (tidy-up item 2), then the payment message, ready, in the same
  // pop-up. pence: what is still to pay, as the server answered it (the message asks for the same).
  const showMessage = (done: Clean, pence: number | null, added: string): void => {
    clear(content)
    title.textContent = `Send ${first} the payment message`
    // Design B (Liam, 26 Oct 2026): Added to Owed on top, who and when on one line, the message as a bubble, the two other
    // answers as picture tiles, and Send pinned to the foot of the pop-up (phone and computer).
    content.append(owedBanner(added, `${first} owes you. Owed will remind you.`),
      el('p', { class: 'muted owed-done-who' }, [name, tidyAddress(done.customer_address ?? cl.customer_address), isToday ? 'done today' : `done ${dayLabel(done.date)}`].filter(Boolean).join(' · ')))
    const target = { id: done.customer_id, name, version: done.customer_version, message_by: done.customer_message_by ?? null, phone: done.customer_phone, email: done.customer_email ?? null }
    const message = paymentBreakdownMessage(done, paymentTodayMessage({ name: done.customer_name, date: done.date, amountPence: pence, job: visitJobLabel(done).toLowerCase() }))
    const subject = `Payment details from ${businessSettings().business_name}`
    const via = (way: string): string => (way === 'text' ? 'by text' : way === 'email' ? 'by email' : 'on WhatsApp')
    const count = (how: string, button: HTMLElement): void => {
      button.setAttribute('disabled', 'disabled')
      void mutate({ label: `Chase 1: ${name} ${done.date}`, method: 'PATCH', path, body: { version: done.version, chased: 1, chase_count: 1, chased_on: today } }).then((ack) => {
        if (!current()) return
        if (!ack.ok && ack.status >= 400) { button.removeAttribute('disabled'); alert4xx(ack); return }
        doneSaved = null
        if (dialog.isConnected) dialog.close()
        undoPatch({ text: `Counted the payment message ${how} to ${name}`, label: `Undo chase: ${name} ${done.date}`, path, ack, version: done.version,
          body: { chased: 0, chase_count: 0, chased_on: null }, after: refreshAfterDone })
      })
    }
    const control = sendControl(target, reminderDrafts.get(`visit:${done.id}`) ?? message, subject, {
      label: (way) => `Send ${via(way)}`,
      manualSend: true, className: 'btn-whatsapp owed-chase',
      changed: () => { if (dialog.isConnected) dialog.close() },
      onConflict: () => { if (dialog.isConnected) dialog.close() },
    })
    const breakdown = owedBreakdown(done, {
      onVisitSaved: (saved) => {
        done.version = saved.version
        if (doneSaved?.ack.row) doneSaved.ack.row = { ...doneSaved.ack.row, version: saved.version }
      },
      onSaved: (fresh, owed) => {
        const draft = reminderDrafts.get(`visit:${done.id}`), prefix = paymentBreakdownPrefix(done)
        if (draft !== undefined && draft !== message) reminderDrafts.set(`visit:${done.id}`, paymentBreakdownPrefix(fresh) + (prefix && draft.startsWith(prefix) ? draft.slice(prefix.length) : draft))
        else reminderDrafts.delete(`visit:${done.id}`)
        if (fresh.payment_status === 'paid' || owed === 0) {
          dialog.close()
          toast('Nothing left to pay. The payment message is no longer needed.')
          return
        }
        showMessage(fresh, owed, `Added to Owed · ${money(owed)}`)
      },
    })
    content.append(breakdown.node)
    control.root.addEventListener('click', breakdown.guard, true)
    control.root.addEventListener('auxclick', breakdown.guard, true)
    const link = control.link
    if (link) {
      const way = control.send.way
      bindEmailSend(link, () => link.dataset.busy ? null : {
        href: link.getAttribute('href') ?? '', sent: () => { link.dataset.busy = '1'; count(via(way), link) },
      })

    }
    content.append(bubblePreview(`visit:${done.id}`, message, control, target, subject, 'payment_today', first))
    const tile = (label: string, line: string, drawing: Drawing, act: (b: HTMLButtonElement) => void): HTMLButtonElement => {
      // The line under the name is drawn by the page (data-line), so the button's words stay its name ("Not now").
      const b = el('button', { type: 'button', class: 'owed-done-tile', title: line }, el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 17)),
        el('span', { class: 'owed-tile-words', 'data-line': line }, el('strong', {}, label))) as HTMLButtonElement
      b.addEventListener('click', () => act(b))
      return b
    }
    content.append(el('div', { class: 'owed-done-tiles' },
      tile('I sent it myself', 'Count it as sent', 'tick', (b) => count('by hand', b)),
      tile('Not now', 'Kept ready in Owed', 'hourglass', () => dialog.close())),
      el('p', { class: 'owed-help' }, !onAPhone()
        ? 'Send it in your app. If Did it send? appears, answer it here.'
        : 'Opening your messaging app counts it as sent. It does not confirm delivery.'), notDoneButton())
    // Send, pinned to the foot (Liam: "button is not sticky which should be standard throught the app and maybe even desktop").
    // Last in the pop-up's content, so the next step's clear takes it too.
    content.append(el('footer', { class: 'booking-footer owed-done-foot' }, el('div', { class: 'owed-done-send' }, control.root)))
  }
  // Done by mistake: back to booked, as the Done's own Undo would (owed-today #3).
  function notDoneButton(): HTMLButtonElement {
    const notDone = el('button', { type: 'button', class: 'link-button owed-done-undo' }, 'Not done after all? Put it back')
    notDone.addEventListener('click', () => {
      const saved = doneSaved
      if (!saved) return
      notDone.disabled = true
      const savedRow = saved.ack.row as { version?: number } | undefined
      void mutate({ label: `Undo done: ${name} ${cl.date}`, method: 'PATCH', path, body: { version: typeof savedRow?.version === 'number' ? savedRow.version : cl.version + 1, ...doneBack } }).then((back) => {
        if (!back.ok && back.status >= 400) { notDone.disabled = false; alert4xx(back); return }
        doneSaved = null
        if (dialog.isConnected) dialog.close()
        toast(`${name} is back to booked, not done.`)
      })
    })
    return notDone
  }
  // Marked done, not paid, by the Job done form (openPaymentAfterDone): straight to what that save answered, once the
  // pop-up is on the page (what it answered is drawn only into a pop-up still there).
  if (opts.saved) {
    clear(content)
    title.textContent = `Marked ${first} done`
    content.append(who, el('p', { class: 'owed-done-visit' }, `${isToday ? 'Done today' : `Done ${dayLabel(cl.date)}`} · not paid yet`))
    doneSaved = { ack: opts.saved, text: `Marked ${name} done, not paid yet` }
  }
  document.body.append(dialog)
  dialog.showModal()
  if (opts.saved) void answered(opts.saved.row as Clean | undefined, { notice: opts.saved.money ?? null })
}
function renderOpen(cl: Clean, container: HTMLElement): void {
  clear(container)
  container.classList.add('owed-tile')
  const who = cl.customer_name ?? `#${cl.customer_id}`
  container.append(el('div', { class: 'owed-card-top' }, el('div', { class: 'owed-card-who' }, el('a', { href: `#/customer/${cl.customer_id}`, class: 'owed-who' }, who),
    ...(cl.customer_address ? [el('span', { class: 'owed-card-addr' }, cl.customer_address)] : []))))
  container.append(el('p', { class: 'owed-open-line' }, `Booked today${shownVisitTime(cl) ? `, ${shownVisitTime(cl)}` : ''}${cl.worker ? ` · ${cl.worker}` : ''}. Nobody has marked it done yet.`))
  const actions = el('div', { class: 'owed-acts' })
  const done = el('button', { type: 'button', class: 'btn-primary owed-open-done' }, 'Done')
  done.addEventListener('click', () => openDone(cl))
  // Not done beside Done, as on the row (Astra: the opened card, and every computer, had no way to it). The Diary's word
  // for the same pop-up (Liam, 23 Oct 2026: one name, not "Didn't happen" here and "Not done" there).
  const no = el('button', { type: 'button', class: 'btn-secondary owed-open-notdone' }, 'Not done')
  no.addEventListener('click', () => { window.location.hash = `#/schedule?notdone=${cl.id}&from=${cl.date}` })
  actions.append(el('div', { class: 'owed-open-pair' }, done, no))
  const diary = el('button', { type: 'button', class: 'owed-more owed-open-diary' }, 'Open diary')
  diary.addEventListener('click', () => { void import('./schedule').then(({ focusDiary }) => { focusDiary([cl.id], cl.date, null, false); window.location.hash = '#/schedule' }) })
  // Contact where Call and Message were (Liam, 9 Oct 2026: "add message next to call"; 21 Oct 2026: Call behind one
  // Contact everywhere, "so the user gets used to that"; contact-tile.ts): Call and their ways to message, opening
  // under the buttons. Only Call reaching them: Call, as before.
  const contact = contactButton({ name: who, phone: cl.customer_phone, email: cl.customer_email ?? null, message_by: cl.customer_message_by ?? null }, 'owed-call-btn')
  if (contact) actions.append(contact.button)
  // Last, so Contact and Open diary share a row (pairUp); with only Call, Call and Open diary do.
  actions.append(diary)
  pairUp(actions)
  container.append(actions)
  if (contact?.choices) container.append(contact.choices)
}

// Each list's words and colour for the phone's pill: the payment words and colours every screen now shares.
const STAGE_PILL: Record<string, [string, string]> = {
  open: ['Not marked done', 'unknown'], invoice: ['Invoice to send', 'unknown'], today: ['Not paid', 'unpaid'], send: ['Not paid', 'unpaid'],
  waiting: ['Chased · not paid', 'chased'], call: ['Chased · not paid', 'chased'], unrecorded: ['Payment not recorded', 'unknown'], cash: ['Confirm amount', 'part'],
}
function groupHead(stage: string, label: string, records: QueueRecord[]): HTMLElement[] {
  const pence = records.reduce((n, r) => n + (r.amount ?? 0), 0)
  return [el('span', { class: 'owed-group-title' }, el('span', { class: `owed-group-dot owed-group-dot-${stage}`, 'aria-hidden': 'true' }), label),
    el('span', { class: 'owed-group-count' }, `${records.length}${pence ? ` · ${money(pence)}` : ''}`)]
}
// Invoices to customers (0065): a visit on a sent invoice is chased through its invoice, never on its own (no customer
// is chased twice); the invoices still to be paid have their own list at the top, late first.
interface OpenInvoice { id: number; number: string; customer_id: number; customer_name: string; total_pence: number; remaining_pence?: number; due_on: string | null; state: InvoiceState; issued_on?: string | null; sent_at?: string | null; unpaid_clean_ids?: number[] }
// Owed design B (Liam, 3 Nov 2026: "on owed invoices take all the real estate surely it should just be like all the owed
// but maybe a tag? Plus a pill to click or filter"; https://claude.ai/artifact/7kiXMNGAq9C3YRv3izeadC): each invoice still
// to be paid is an ordinary Owed row with an Invoice tag, in To chase when late, else Waiting, and an Invoices tile shows
// only them (tap again for everyone). The Original layout keeps its Invoices to be paid list.
const invoiceStage = (inv: OpenInvoice): 'send' | 'waiting' => (inv.state === 'late' ? 'send' : 'waiting')
const invoiceToPay = (inv: OpenInvoice): number => inv.remaining_pence ?? inv.total_pence
function matchesInvoice(inv: OpenInvoice, q: string): boolean {
  return !q || `${inv.customer_name} ${inv.number}`.toLowerCase().includes(q)
}
// Cash or Bank marks the whole invoice paid, with Undo; Cash on a round that asks what was taken asks here too.
function payInvoice(inv: OpenInvoice, m: 'cash' | 'bank', host: HTMLElement, buttons: HTMLButtonElement[]): void {
  const run = (taken: number | null): void => {
    for (const x of buttons) x.disabled = true
    void payInvoiceFromOwed(inv.id, m, taken).then(({ number, undo, failed, marked, stillToPay }) => {
      // As a visit's payment: the total counts down, and the last one owing shows All paid up.
      if (marked) { justPaidAt = Date.now(); countFromPence = combinedTotal() }
      void load()
      if (failed) toast(marked ? `Only part of ${number} was marked paid: ${failed}` : failed)
      if (marked) offerUndo(`${inv.customer_name}: ${number} ${failed ? 'partly ' : ''}paid ${m === 'cash' ? 'in cash' : 'by bank'}${stillToPay ? `, ${money(stillToPay)} still to pay` : ''}`, () => { void undo().then(() => void load()) })
      else if (!failed) toast(`Nothing marked paid on ${number}`)
    }).catch((e: unknown) => {
      void load()
      for (const x of buttons) x.disabled = false
      toast((e as Error)?.message ?? 'It did not save. Try again.')
    })
  }
  if (m === 'cash' && getMe()?.broughtMoney === true) askCashTaken(host, invoiceToPay(inv), (pence) => run(pence)); else run(null)
}
let openInvoices: OpenInvoice[] = []
type OnInvoice = { on_invoice?: string | null }
function withoutInvoiced(p: OwedPayload): OwedPayload {
  const keep = (list: Clean[]): Clean[] => list.filter((c) => !(c as Clean & OnInvoice).on_invoice)
  return { ...p, cleans: keep(p.cleans), unrecorded: { ...p.unrecorded, cleans: keep(p.unrecorded?.cleans ?? []) } }
}
let invoicesUnread: Clean[] = []
// Owed's read as it came, invoiced visits included, for Send all (it says why they are left out).
let lastRead: OwedPayload | null = null
// WhatsApp from Roundly (Stage 2): Send all's facts (on, the approved wordings, the stopped numbers, money revisions),
// read with Owed when the round sends payment messages from Roundly, so one visit's Send can go from Roundly too.
let waFacts: SendAllFacts | null = null

// Each visit's send from Roundly keeps its request key until it is known to have gone or not: a tap again after no
// signal is the same request, so it can never go twice (as Chats and Send all).
const waKeys = new Map<string, string>()
async function sendFromRoundly(cl: Clean, words: string, job: string | null, button: HTMLElement): Promise<void> {
  const slot = `${cl.id}:${chaseCountOf(cl)}:${cl.version}`
  const key = waKeys.get(slot) ?? crypto.randomUUID()
  waKeys.set(slot, key)
  const rev = waFacts?.revs.find((r) => r.customer_id === cl.customer_id)?.rev ?? 0
  const body = {
    customer_id: cl.customer_id,
    visits: [{ id: cl.id, stage: chaseCountOf(cl), gen: (cl as Clean & { chase_gen?: number }).chase_gen ?? 0, version: cl.version, to_collect_pence: toCollect(cl), money_rev: rev }],
    jobs: job ? { [String(cl.id)]: job } : {}, fingerprint: wordsPrint(words), batch: null, way: 'whatsapp',
  }
  // The button's own words, not its WhatsApp mark.
  const words0 = button.querySelector<HTMLElement>('span:last-child') ?? button
  const was = words0.textContent
  words0.textContent = 'Sending…'
  try {
    const r = await changeNow<{ send?: { state?: string } }>('POST', '/api/chase-sends', body, key)
    waKeys.delete(slot)
    toast(r.send?.state === 'accepted' ? `Sent to ${cl.customer_name ?? 'them'} from Roundly.` : "Roundly isn't sure it went. Check WhatsApp on the work phone.")
  } catch (err) {
    const e = err instanceof ApiError ? err : null
    // No signal: kept, so the next tap is the same send. Anything Roundly answered frees it.
    if (!e || e.status !== 0) waKeys.delete(slot)
    toast(e?.status === 0 ? "No signal, so it hasn't gone. Tap Send again when you're connected: it won't go twice." : e?.message ?? "That didn't send. Try again.")
  } finally {
    words0.textContent = was
    delete button.dataset.busy
    void load()
  }
}

// One visit's payment message or reminder from Roundly on WhatsApp, when everything allows it: the customer's way is
// WhatsApp, a WhatsApp number not stopped, and Meta approved the very words they would get. Else null (Open WhatsApp).
function waPlanFor(cl: Clean, today: string, job: string | null): { words: string; number: string } | null {
  const f = waFacts
  if (!f?.whatsapp) return null
  const way = cl.customer_message_by || businessSettings().message_by || 'whatsapp'
  if (way !== 'whatsapp') return null
  const digits = waNumber(cl.customer_phone ?? null)
  if (!digits || (f.wa_stops ?? []).includes(`+${digits}`)) return null
  const w = waSendWords({ name: cl.customer_name, visits: [{ date: cl.date, toCollectPence: toCollect(cl), chaseCount: chaseCountOf(cl), job }], today, settings: businessSettings() })
  if ('problem' in w || !(f.wa_ready ?? []).some((t) => t.kind === w.kind && t.fingerprint === w.shape.fingerprint)) return null
  return { words: w.words, number: `+${digits}` }
}
let invoicesOff = false
function invoicesSection(): HTMLElement | null {
  if (invoicesUnread.length) {
    const box = el('section', { class: 'money-invoices', 'aria-label': 'Invoices to be paid' }, el('h3', {}, 'Invoices to be paid'),
      el('p', { class: 'muted invoice-note' }, invoicesOff ? 'Invoices are switched off. These visits are on invoices already sent:' : "Couldn't load your invoices just now."))
    const again = el('button', { type: 'button', class: 'btn-secondary' }, 'Try again')
    again.addEventListener('click', () => void load())
    if (invoicesOff) again.hidden = true
    const list = el('div', { class: 'invoices-list' })
    for (const cl of invoicesUnread) {
      const live = cl as Clean & LiveInvoice & OnInvoice
      if (!live.live_invoice_id) continue
      const b = el('button', { type: 'button' }, el('span', {}, `${cl.customer_name ?? 'Customer'} · ${live.on_invoice}`), el('span', { class: 'muted' }, `Open ${live.on_invoice}`))
      b.addEventListener('click', () => openInvoice(live.live_invoice_id!, { done: () => void load() }))
      list.append(b)
    }
    box.append(again, list)
    return box
  }
  if (!openInvoices.length) return null
  const box = el('section', { class: 'money-invoices', 'aria-label': 'Invoices to be paid' }, el('h3', {}, 'Invoices to be paid'))
  const list = el('div', { class: 'invoices-list' })
  const order = (i: OpenInvoice): number => (i.state === 'late' ? 0 : 1)
  for (const inv of [...openInvoices].sort((a, b) => order(a) - order(b) || (a.due_on ?? '').localeCompare(b.due_on ?? ''))) list.append(invoiceCard(inv))
  box.append(list)
  return box
}

// One invoice to be paid, as every other Owed card (Liam, 26 Oct 2026: Joel's invoice "doesn't look part of the same app";
// https://claude.ai/artifact/9NridJXtvQsvtS9Z4z9XFo): who and the invoice, what is still to pay with its state, the
// Reminder tile (the invoice opens with Send PDF and its reminder words), and Cash and Bank marking the whole invoice paid,
// with Undo. "Open the invoice" for the rest (cancel, credit note, its link).
function invoiceCard(inv: OpenInvoice): HTMLElement {
  const card = el('section', { class: 'owed-inv-card', 'aria-label': `${inv.customer_name}, invoice ${inv.number}` })
  const toPay = inv.remaining_pence ?? inv.total_pence
  const part = inv.remaining_pence != null && inv.remaining_pence < inv.total_pence
  card.append(el('div', { class: 'owed-card-top' },
    el('div', { class: 'owed-card-who' }, el('a', { href: `#/customer/${inv.customer_id}`, class: 'owed-who' }, inv.customer_name),
      el('span', { class: 'owed-card-addr' }, `Invoice ${inv.number}`),
      el('span', { class: 'owed-inv-when' }, [part ? `${money(inv.total_pence - toPay)} paid of ${money(inv.total_pence)}` : null, inv.due_on ? `pay by ${shortDate(inv.due_on)}` : null].filter(Boolean).join(' · '))),
    el('div', { class: 'owed-inv-right' }, el('strong', {}, money(toPay)), stateChip(inv.state))))
  const words = (title: string, line: string): HTMLElement => el('span', { class: 'owed-tile-words' }, el('strong', {}, title), el('span', {}, line))
  const reminder = el('button', { type: 'button', class: 'owed-inv-tile owed-inv-reminder', 'aria-label': `Reminder for ${inv.customer_name}` },
    el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing('bubble', 19)), words('Reminder', 'See it and send it, with the PDF'), lineDrawing('chevronRight', 18))
  reminder.addEventListener('click', () => openInvoice(inv.id, { done: () => void load() }))
  const pay = el('div', { class: 'owed-inv-pay', role: 'group', 'aria-label': `Paid, for ${inv.customer_name}` })
  for (const m of ['cash', 'bank'] as const) {
    const b = el('button', { type: 'button', class: `owed-inv-tile owed-inv-${m}` }, el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing(m === 'cash' ? 'pound' : 'bank', 17)),
      words(m === 'cash' ? 'Cash' : 'Bank', m === 'cash' ? 'Paid in cash' : 'Paid by transfer')) as HTMLButtonElement
    b.addEventListener('click', () => payInvoice(inv, m, card, Array.from(pay.querySelectorAll('button'))))
    pay.append(b)
  }
  const open = el('button', { type: 'button', class: 'link-button owed-inv-open' }, 'Open the invoice')
  open.addEventListener('click', () => openInvoice(inv.id, { done: () => void load() }))
  card.append(reminder, pay, open)
  return card
}

function render(data: OwedPayload, root: HTMLElement, fresh = false, arrive = false): void {
  classic = owedLayout() === 'original'
  if (classic && filterMode === 'invoices') filterMode = 'all'
  if (classic) { renderClassic(data, root, fresh); const inv = invoicesSection(); if (inv) root.prepend(inv); return }
  closeMenu(); clear(root)
  const page = el('div', { class: `owed-page${owedLayout() === 'original' ? ' owed-original' : ''}` }); root.append(page)
  const today = todayISO(), brought = data.brought
  if (fresh) setOwedBadge(data, today)
  // The top as Messages has it (Liam, 8 Oct 2026): the title, a yellow label with what is owed and the layout switch side
  // by side; the bank statement link on the right where Messages has Focus.
  const owedNow = data.total_pence + (brought?.total_pence ?? 0)
  // Paid today (marking paid made satisfying, 10 Oct 2026): what was marked paid since midnight, every phone's; a payment
  // flies into it (paid-moment.ts flyToPaidToday).
  const paidToday = data.paid_today?.pence ?? 0
  const paidTodayFigure = el('strong', { 'data-pence': String(paidToday) }, money(paidToday))
  // Design B (Liam, 23 Oct 2026: "too much space is used before we get to messages", then "Go"): the title with the bank
  // statement as a picture button, "£X owed" and Paid today as two chips under it, then the lists as picture tiles.
  const total = el('strong', { class: 'owed-total' }, money(owedNow))
  const title = el('div', { class: 'owed-title' }, el('h1', {}, 'Owed'), ...(owedNow > 0 ? [el('span', { class: 'owed-total-pill' }, total, ' owed')] : []),
    el('span', { class: 'owed-paid-today', id: 'owed-paid-today' }, el('small', {}, 'Paid today'), paidTodayFigure))
  const bank = el('a', { href: '#/bank', class: 'owed-bank', 'aria-label': 'Tick off a bank statement' }, el('span', { class: 'owed-bank-ring', 'aria-hidden': 'true' }, lineDrawing('bank', 17)), el('span', {}, 'Bank statement'))
  const heading = el('div', { class: 'owed-heading' }, el('div', { class: 'owed-heading-main' }, title), bank)
  page.append(heading)
  // Unreadable invoices still say so at the top; readable ones are rows below (design B).
  if (invoicesUnread.length) { const inv = invoicesSection(); if (inv) page.append(inv) }
  // Send all payment texts (0093; docs/plans/send-all-texts/PLAN.md, B2): the card at the top, only once texting is
  // connected and the owner has said the agreement line. Read from the visits as Owed read them, invoices included.
  if (sendAllOn()) { const slot = el('div', { class: 'owed-send-all' }); page.append(slot); const cleans = [...(lastRead?.cleans ?? data.cleans)]; void import('../send-all-sheet').then((m) => m.fillSendAllSlot(slot, cleans, () => { if (onOwed()) void load() })).catch(() => { /* no signal for Send all's screens: nothing shows */ }) }
  const views = new Map<string, { item: HTMLElement; button: HTMLButtonElement; card: HTMLElement | null; record: QueueRecord }>()
  let applyLayout: (layout: OwedLayout) => void = (layout) => page.classList.toggle('owed-original', layout === 'original')
  const headline = data.total_pence + (brought?.total_pence ?? 0)
  const from = countFromPence; countFromPence = null
  if (from !== null && from !== headline) countBetween(total, (k) => money(Math.round(from + (headline - from) * k)), 180)
  const counts = owedCounts(data, today)
  const invoiceRows = invoicesHere() && !invoicesUnread.length ? openInvoices : []
  counts.send += invoiceRows.filter((i) => invoiceStage(i) === 'send').length
  counts.waiting += invoiceRows.filter((i) => invoiceStage(i) === 'waiting').length
  const invoicePence = (stage?: OwedStage): number => invoiceRows.filter((i) => !stage || invoiceStage(i) === stage).reduce((n, i) => n + invoiceToPay(i), 0)
  const stagePence = (stage: OwedStage): number => stage === 'unrecorded' ? data.unrecorded?.total_pence ?? 0
    : data.cleans.filter((cl) => owedStageOf(cl, today) === stage).reduce((n, cl) => n + (toCollect(cl) ?? 0), 0) + (brought?.accounts ?? []).filter((a) => accountStage(a, today) === stage).reduce((n, a) => n + a.chaseable_pence, 0) + invoicePence(stage)
  // The lists as picture tiles with their money and how many: tap one to show just those, tap it again for all. A list
  // with nobody in it shows no tile (Liam: "dont know the point of call and not recorded"; they appear when they matter).
  // They replace the totals card and the Show list, which said the same twice.
  const tiles = el('div', { class: 'owed-tiles', role: 'group', 'aria-label': 'Show' })
  for (const { value, label } of OWED_FILTERS) {
    if (value === 'all' || (counts[value] === 0 && filterMode !== value)) continue
    const n = counts[value], pence = stagePence(value as OwedStage), on = filterMode === value
    const tile = el('button', { type: 'button', class: 'owed-tile', 'data-filter': value, 'aria-pressed': String(on), 'aria-label': `${label}: ${money(pence)}, ${n} ${n === 1 ? 'record' : 'records'}${on ? '. Showing just these; tap again for all' : ''}` },
      value === 'waiting' ? meaningIcon('waiting', 'tile')
        : el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing(OWED_TILE_DRAWINGS[value] ?? 'dots', 18)),
      el('span', { class: 'owed-tile-words' }, el('strong', {}, money(pence)), el('span', {}, `${label} · ${n}`)))
    tile.addEventListener('click', () => {
      filterMode = filterMode === value ? 'all' : value
      render(data, root)
      root.querySelector<HTMLButtonElement>(`.owed-tile[data-filter="${value}"]`)?.focus({ preventScroll: true })
    })
    tiles.append(tile)
  }
  if (invoiceRows.length || filterMode === 'invoices') {
    const n = invoiceRows.length, pence = invoicePence(), on = filterMode === 'invoices'
    const tile = el('button', { type: 'button', class: 'owed-tile', 'data-filter': 'invoices', 'aria-pressed': String(on), 'aria-label': `Invoices: ${money(pence)}, ${n} ${n === 1 ? 'invoice' : 'invoices'}${on ? '. Showing just these; tap again for all' : ''}` },
      el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing('invoiceAdd', 18)),
      el('span', { class: 'owed-tile-words' }, el('strong', {}, money(pence)), el('span', {}, `Invoices · ${n}`)))
    tile.addEventListener('click', () => {
      filterMode = on ? 'all' : 'invoices'
      render(data, root)
      root.querySelector<HTMLButtonElement>('.owed-tile[data-filter="invoices"]')?.focus({ preventScroll: true })
    })
    tiles.append(tile)
  }
  if (tiles.firstChild) page.append(tiles)
  const bar = el('div', { class: 'owed-toolbar' })
  const search = el('input', { type: 'search', class: 'search-box', placeholder: 'Name, address or phone', 'aria-label': 'Search owed' })
  search.value = filterText
  search.addEventListener('input', () => { const at = search.selectionStart; filterText = search.value; render(data, root); const next = root.querySelector<HTMLInputElement>('input[type="search"]'); next?.focus(); if (at !== null) next?.setSelectionRange(at, at) })
  const sort = el('select', { 'aria-label': 'Sort owed', class: 'owed-sort' })
  for (const [value, label] of [['newest', 'Newest first'], ['oldest', 'Oldest first'], ['amount', 'Highest amount']]) sort.append(el('option', { value }, label))
  sort.value = sortMode; sort.addEventListener('change', () => { sortMode = sort.value; render(data, root); triggerFor(root, 'Sort owed')?.focus({ preventScroll: true }) })
  // Search, with Sort beside it (its label kept for screen readers); the tiles above are what Show was.
  bar.append(search, el('label', { class: 'owed-filter owed-sort-filter' }, el('span', { class: 'sr-only' }, 'Sort'), sort)); page.append(bar); styleSelect(sort)
  const records: QueueRecord[] = []
  if (filterMode === 'all') {
    const now = clockNow()
    for (const cl of data.today_open ?? []) {
      // Today's once its time has passed; an earlier day's (the last week's, still unanswered) always.
      const earlier = cl.date < today
      if ((!earlier && !shouldBeDone(visibleTime(cl), now)) || !matchesOwed(cl, filterText.trim().toLowerCase())) continue
      records.push({ key: `open:${cl.id}`, name: cl.customer_name ?? 'Customer', address: cl.customer_address ?? '', date: cl.date, amount: cl.amount_pence ?? null, stage: 'open',
        paths: [`/api/cleans/${cl.id}`], moneyLabel: 'Not marked done', context: '', line: `${earlier ? dayLabel(cl.date) : 'today'}${shownVisitTime(cl) ? `, ${shownVisitTime(cl)}` : ''} · not marked done yet`,
        render: (card) => renderOpen(cl, card), markDone: () => openDone(cl),
        // Didn't happen (Liam, 22 Oct 2026: "Theres no way to mark move or rebook or not done ... its not easy to move them
        // from owed"): the Diary on that day with its Not done choices open (Move, Couldn't do it, Cancel, Off the round).
        notDone: () => { window.location.hash = `#/schedule?notdone=${cl.id}&from=${cl.date}` } })
    }
  }
  for (const cl of [...data.cleans, ...(data.unrecorded?.cleans ?? [])]) {
    let stage: string = owedStageOf(cl, today)
    if ((filterMode !== 'all' && stage !== filterMode) || !matchesOwed(cl, filterText.trim().toLowerCase())) continue
    // Done in the last week and no message sent yet: "Payment message to send", the payment message ready.
    if (stage === 'send' && paymentMessageDue(cl.date, today) && chaseCountOf(cl) === 0) stage = 'today'
    records.push({ key: `visit:${cl.id}`, name: cl.customer_name ?? 'Customer', address: cl.customer_address ?? '', date: cl.date, amount: toCollect(cl), stage,
      paths: [`/api/cleans/${cl.id}`], moneyLabel: cl.payment_status === 'unknown' ? 'Payment not recorded' : 'Still to pay', context: hasExtraJobs(cl.customer_id) ? visitJobLabel(cl) : '',
      // The pill in the visit's own payment words and colour, as the Diary's (GPT-6.1: a part paid one read "Not paid").
      pill: [paymentWords(cl), paymentLabel(cl).className.replace('badge-', '')],
      // The shared words, as every row (docs/DIARY-AND-MESSAGES.md); an "Invoice to send" row says its invoice on its own
      // lemon button below (invoiceActOf).
      line: `visit ${shortDate(cl.date)} · ${visitWords(cl, null, today).text}`, render: (card) => renderRow(cl, card, today), pay: (m, host, btn, from) => payVisit(cl, m, host, btn, today, from),
      // A WhatsApp since the last chase, or chasing paused (0081, item 67), said on the row itself too.
      ...(cl.payment_status === 'unpaid' && (sinceChase(cl) || cl.chase_paused_at) ? { held: { since: sinceChase(cl), paused: !!cl.chase_paused_at, customerId: cl.customer_id } } : {}),
      ...(stage === 'invoice' ? { invoice: invoiceActOf(cl) } : {}) })
    // Looks like a business: the row holds while a change to the customer is on its way too (Astra 4).
    // Every row of a customer whose words say business holds while a change to them is on its way, asked or not, so a
    // payment message can't go while Send invoice waits for signal (Astra 4, Sol 2).
    const hint = hintFor(cl, stage)
    const r = records[records.length - 1]
    if (hint) r.business = hint
    if (hint || businessReason({ name: cl.customer_name, address: cl.customer_address })) r.paths = [...r.paths, `/api/customers/${cl.customer_id}`]
  }
  for (const a of brought?.accounts ?? []) {
    const stage = accountStage(a, today)
    if (filterMode !== 'all' && stage !== filterMode) continue
    if (!matchesOwed({ customer_name: a.customer_name, customer_address: a.customer_address, customer_phone: a.customer_phone } as Clean, filterText.trim().toLowerCase())) continue
    records.push({ key: `account:${a.id}`, name: a.customer_name ?? 'Customer', address: a.customer_address ?? '', date: '', amount: a.left_pence || a.collecting_pence, stage,
      paths: [`/api/brought-accounts/${a.id}`, ...a.collections.map((c) => `/api/brought-collections/${c.id}`)], moneyLabel: a.left_pence > 0 ? 'Still to pay' : 'Collecting', context: '',
      line: `Brought account${a.collecting_pence ? ` · ${money(a.collecting_pence)} collecting` : ''}`, render: (card) => renderAccount(a, card, today), ...(a.left_pence > 0 ? { pay: (m: 'cash' | 'bank', host: HTMLElement, btn: HTMLElement, from?: HTMLElement) => payAccount(a, m, host, btn, from) } : {}) })
  }
  for (const inv of invoiceRows) {
    const stage = invoiceStage(inv)
    if ((filterMode !== 'all' && filterMode !== 'invoices' && stage !== filterMode) || !matchesInvoice(inv, filterText.trim().toLowerCase())) continue
    const visits = inv.unpaid_clean_ids?.length ?? 0
    const sent = (inv.sent_at ?? inv.issued_on ?? '').slice(0, 10)
    records.push({ key: `invoice:${inv.id}`, name: inv.customer_name, address: `${inv.number}${visits ? ` · ${visits} ${visits === 1 ? 'visit' : 'visits'}` : ''}`, date: sent || inv.due_on || '',
      amount: invoiceToPay(inv), stage: filterMode === 'invoices' ? 'invoices' : stage, paths: [`/api/invoices/${inv.id}`, ...(inv.unpaid_clean_ids ?? []).map((id) => `/api/cleans/${id}`)], moneyLabel: 'Still to pay', context: 'Invoice',
      pill: inv.state === 'late' ? ['Late', 'unpaid'] : inv.state === 'part_paid' ? ['Part paid', 'topay'] : ['Not paid', 'unpaid'],
      line: [sent ? `sent ${shortDate(sent)}` : null, inv.due_on ? `pay by ${shortDate(inv.due_on)}` : null].filter(Boolean).join(' · ') || `Invoice ${inv.number}`,
      // The row already has Reminder, Cash and Bank; opened, the card is the whole invoice card (the computer shows it
      // beside the list) so cancel, credit note and its link stay one tap away.
      render: (card) => { card.append(invoiceCard(inv)) },
      remind: () => openInvoice(inv.id, { done: () => void load() }),
      pay: (m, host, btn) => payInvoice(inv, m, host, [btn as HTMLButtonElement, ...Array.from(host.querySelectorAll<HTMLButtonElement>('.owed-row-pay-btn'))]) })
  }
  if (filterMode === 'all') for (const cash of brought?.unconfirmed_cash ?? []) {
    if (!matchesOwed({ customer_name: cash.customer_name } as Clean, filterText.trim().toLowerCase())) continue
    records.push({ key: `cash:${cash.id}`, name: cash.customer_name ?? 'Customer', address: '', date: cash.received_on, amount: null, stage: 'cash', paths: [`/api/money-receipts/${cash.id}`], context: '', line: 'Cash amount to confirm', render: (card) => card.append(confirmCashRow(cash)) })
  }
  if (filterText.trim()) {
    search.classList.add('search-active')
    const everyone = el('button', { type: 'button', class: 'link-button owed-search-clear' }, 'Show everyone')
    everyone.addEventListener('click', () => { filterText = ''; render(data, root); root.querySelector<HTMLInputElement>('.owed-toolbar input[type="search"]')?.focus() })
    bar.append(el('div', { class: 'owed-search-on', role: 'status' }, el('span', {}, 'Showing only people matching ', el('strong', {}, `“${filterText.trim()}”`), ` · ${records.length} shown`), everyone))
  }
  const workspace = el('div', { class: 'owed-workspace' }), queue = el('section', { class: 'owed-queue', 'aria-label': 'Owed records' }), slot = el('aside', { class: 'owed-detail-slot', 'aria-label': 'Selected record' })
  workspace.append(queue, slot); page.append(workspace)
  // The last one owing just paid: All paid up, with the day's figure (paid-moment.ts), once, above anything still to
  // answer (a "Done?" visit owes nothing yet).
  // "Payment not recorded" visits are for checking, not money anyone said is owed, so they do not hold it back.
  const paidUp = Date.now() - justPaidAt < 8000 && !filterText && filterMode === 'all' && !records.some((r) => r.pay && r.stage !== 'unrecorded')
  if (paidUp) justPaidAt = 0
  if (paidUp) allPaidUp(queue, data.paid_today?.pence ?? null)
  if (!records.length) { if (!paidUp) queue.append(el('p', { class: 'empty-note' }, filterText ? 'No records match that search.' : owedEmptyText(filterMode))); return }
  const makeCard = (record: QueueRecord): HTMLElement => {
      const card = el('div', { class: `owed-selected-card${arrive ? ' owed-card-arriving' : ''}`, 'data-paths': JSON.stringify(record.paths), 'data-owed-card-key': record.key }); record.render(card)
      card.addEventListener('focusin', () => { if (owedLayout() === 'original') selected = record.key })
      const pending = el('p', { class: 'owed-pending-status', role: 'status' }, 'Pending · kept on this phone. The balance changes after acknowledgement.'); pending.hidden = true; card.append(pending)
      const holdPending = (e: Event): void => { const target = e.target as HTMLElement; if (isPending(record.paths) && target.closest('button, .send-way a')) { e.preventDefault(); e.stopImmediatePropagation() } }
      card.addEventListener('click', holdPending, true); card.addEventListener('auxclick', holdPending, true)
      card.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter' && isPending(record.paths)) { e.preventDefault(); e.stopImmediatePropagation() } }, true)
      return card
  }
  const narrow = typeof matchMedia === 'function' && matchMedia('(max-width: 850px)').matches
  let nudged = false
  for (const [stage, label] of filterMode === 'invoices' ? [['invoices', 'Invoices'] as [string, string]] : OWED_GROUPS) {
    const groupRecords = records.filter((r) => r.stage === stage).sort((a, b) => sortMode === 'amount' ? (b.amount ?? -1) - (a.amount ?? -1) : sortMode === 'oldest' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date))
    if (!groupRecords.length) continue
    const group = stage === 'unrecorded' && filterMode === 'all' ? el('details', { class: 'owed-group owed-unknown-group' }) : el('section', { class: 'owed-group' })
    if (group.tagName === 'DETAILS') { (group as HTMLDetailsElement).open = !!filterText || groupRecords.some((r) => r.key === selected); group.append(el('summary', {}, ...groupHead(stage, label, groupRecords))) }
    else group.append(el('h2', {}, ...groupHead(stage, label, groupRecords)))
    const list = el('div', { class: 'owed-queue-list' }); group.append(list); queue.append(group)
    for (const record of groupRecords) {
      const item = el('div', { class: `owed-queue-item owed-stage-${record.stage}${record.key === selected ? ' owed-selected' : ''}`, 'data-record': record.key })
      const button = el('button', { type: 'button', class: 'owed-queue-button', 'aria-expanded': String(record.key === selected), 'data-select': record.key },
        el('span', { class: 'owed-avatar', 'aria-hidden': 'true' }, record.name.split(/\s+/).slice(0, 2).map((s) => s[0]).join('')),
        el('span', { class: 'owed-queue-copy' }, el('span', { class: 'owed-queue-name' }, el('strong', {}, record.name), ...(record.context ? [el('span', { class: 'owed-queue-tag' }, record.context)] : [])),
          ...(record.address ? [el('small', {}, record.address)] : []),
          el('span', { class: `owed-line${record.stage === 'unrecorded' || record.stage === 'cash' ? ' owed-line-check' : ''}` }, record.line)),
        // The line already says "not paid", so "Still to pay" no longer sits under each amount; the other labels stay.
        el('span', { class: 'owed-queue-money' }, record.amount === null ? 'No figure' : money(record.amount),
          ...(record.stage === 'cash' ? [el('small', {}, 'Confirm amount')] : record.moneyLabel && record.moneyLabel !== 'Still to pay' ? [el('small', {}, record.moneyLabel)] : []),
          // On a phone, where it stands as the Diary's pill says it, in the same colour (docs/plans/one-look/PLAN.md 2, 3).
          // Not on the Done? list: its line already says "not marked done yet" (Sonnet's screens review).
          el('span', { class: `owed-pill owed-pill-${record.pill?.[1] ?? STAGE_PILL[record.stage]?.[1] ?? 'unknown'}` }, record.stage === 'open' ? '' : record.pill?.[0] ?? (record.stage === 'cash' ? 'Confirm amount' : record.moneyLabel && record.moneyLabel !== 'Still to pay' ? record.moneyLabel : STAGE_PILL[record.stage]?.[0] ?? ''))))
      const toggle = (): void => { selected = selected === record.key ? null : record.key; render(data, root, false, selected !== null); const next = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-select]')).find((b) => b.dataset.select === record.key); next?.focus({ preventScroll: true }) }
      button.addEventListener('click', toggle)
      item.append(button); list.append(item)
      // A WhatsApp since the last chase (0081, item 67; picture 10): the line into their chat, under the row; or Chasing paused.
      if (record.held) {
        const { since, paused, customerId } = record.held
        const words = since?.words.replace(/\s+/g, ' ').trim() ?? ''
        item.append(el('div', { class: 'owed-row-held' },
          ...(since ? [el('a', { class: 'owed-wa-since', href: `#/chats?customer=${customerId}` }, `New WhatsApp since you chased${words ? `: "${words.length > 50 ? `${words.slice(0, 47)}…` : words}"` : ''} ›`)] : []),
          ...(paused ? [el('span', { class: 'owed-held-note owed-paused' }, 'Chasing paused')] : [])))
      }
      // "Invoice to send": its own lemon button on the row (Astra, judging the pictures for Liam, 21 Oct 2026); one made
      // and not sent yet opens that invoice.
      if (record.invoice) {
        const act = record.invoice
        const b = el('button', { type: 'button', class: 'btn-primary owed-row-invoice-btn' }, act.label)
        b.addEventListener('click', () => act.run())
        item.append(el('div', { class: 'owed-row-invoice' }, b))
      }
      // One tap to mark paid on a phone, as before 7 Oct (Liam, 8 Oct 2026, option A): Paid · Cash · Bank under the row,
      // the card's own save, Undo after. Shown on narrow screens only, and not while the row's card is open under it.
      // Done (lemon) and Didn't happen side by side, as the Diary's jobs still to do (docs/plans/one-look/PLAN.md 3).
      if (record.markDone) {
        const strip = el('div', { class: 'owed-row-done', role: 'group', 'aria-label': `Finished, ${record.name}?` })
        const b = el('button', { type: 'button', class: 'btn-primary owed-row-done-btn' }, 'Done')
        b.addEventListener('click', () => record.markDone!())
        strip.append(b)
        if (record.notDone) {
          const no = el('button', { type: 'button', class: 'btn-secondary owed-row-notdone-btn' }, 'Not done')
          no.addEventListener('click', () => record.notDone!())
          strip.append(no)
        }
        item.append(strip)
      }
      if (record.pay) {
        const strip = el('div', { class: 'owed-row-pay', role: 'group', 'aria-label': `Paid, for ${record.name}` }, el('span', { class: 'owed-row-pay-label' }, 'Paid'))
        // Design C (Liam, 23 Oct 2026: "C is definetly the one"; https://claude.ai/artifact/7ZiQZgKUEc9CNoDrBHzCJR): picture
        // tiles that say what each does, as the Messages card's. The payment message first, opening the row's card as a tap
        // on the name does ("its not immdeiately clear you click there name and it expands"), then Cash and Bank.
        strip.classList.add('owed-row-tiles')
        const words = (title: string, line: string): HTMLElement => el('span', { class: 'owed-tile-words' }, el('strong', {}, title), el('span', {}, line))
        // Looks like a business (design B): the question, then Send invoice (lemon) beside the payment message.
        // Not on the Original layout, whose open cards ask it themselves.
        const visit = record.business && owedLayout() !== 'original' ? [...data.cleans, ...(data.unrecorded?.cleans ?? [])].find((c) => `visit:${c.id}` === record.key) : undefined
        if (record.business && visit) {
          strip.classList.add('owed-business-area', 'owed-row-business')
          strip.append(hintLine(record.business, record.amount), hintInvoiceButton(visit, record.business, true))
        }
        if (record.remind) {
          // An invoice's reminder opens the invoice itself, with Send PDF and its reminder words.
          const open = el('button', { type: 'button', class: 'owed-row-open-btn owed-row-tile', 'aria-label': `Reminder for ${record.name}` },
            el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing('bubble', 19)), words('Reminder', 'See it and send it, with the PDF'), lineDrawing('chevronRight', 18))
          open.addEventListener('click', () => record.remind!())
          strip.append(open)
        } else if (record.stage === 'today' || record.stage === 'send' || record.stage === 'waiting') {
          const open = el('button', { type: 'button', class: 'owed-row-open-btn owed-row-tile', 'aria-label': `Payment message for ${record.name}` },
            el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, lineDrawing('bubble', 19)),
            words(record.stage === 'today' ? 'Payment message' : 'Reminder', record.stage === 'today' ? 'See it and send it' : 'See it and send it again'), lineDrawing('chevronRight', 18))
          open.addEventListener('click', toggle)
          strip.append(open)
        }
        const ways = new Map<PaidWay, HTMLButtonElement>()
        const payBy = (m: PaidWay, from?: HTMLElement): void => {
          const b = ways.get(m)!
          if (b.disabled || isPending(record.paths) || item.classList.contains('cell-pending')) return
          record.pay!(m, item, b, from)
        }
        for (const m of ['cash', 'bank'] as const) {
          const b = el('button', { type: 'button', class: 'owed-row-pay-btn owed-row-tile', 'aria-label': `Paid ${m}, ${record.name}` }) as HTMLButtonElement
          paidLook(b, m)
          const icon = b.querySelector('.paid-icon')
          b.textContent = ''
          b.append(el('span', { class: 'owed-tile-ring', 'aria-hidden': 'true' }, ...(icon ? [icon] : [])), words(m === 'cash' ? 'Cash' : 'Bank', m === 'cash' ? 'Paid in cash' : 'Paid by transfer'))
          b.addEventListener('click', () => payBy(m))
          ways.set(m, b)
          strip.append(b)
        }
        if (record.business && visit) strip.append(hintDismissals(visit, record.business))
        item.append(strip)
        // Slide to pay on a phone (Liam approved B, 10 Oct 2026): right for Cash, left for Bank; the buttons stay.
        if (narrow && record.key !== selected && owedLayout() !== 'original') {
          const amount = record.amount === null ? '' : money(record.amount)
          slideToPay(item, amount, (m, from) => payBy(m, from))
          if (!nudged) { nudged = true; nudgeSlideOnce(item, amount) }
        }
      }
      const view = { item, button, card: null as HTMLElement | null, record }; views.set(record.key, view)
      if (record.key !== selected && owedLayout() !== 'original') continue
      const card = makeCard(record)
      view.card = card
      if (narrow || owedLayout() === 'original') item.append(card); else slot.append(card)
    }
  }
  if (!slot.childElementCount && !narrow) slot.append(el('div', { class: 'owed-selection-hint' }, 'Choose a record to review its payment and reminder.'))
  if (landOn) {
    const view = views.get(landOn)
    landOn = null
    if (view) { view.item.scrollIntoView?.({ block: 'center' }); landFlash(view.item) }
    else toast('That visit is not owed any more.')
  }
  applyLayout = (layout): void => {
    page.classList.toggle('owed-original', layout === 'original')
    if (layout === 'new' && !views.has(selected ?? '')) selected = views.keys().next().value ?? null
    const inline = typeof matchMedia === 'function' && matchMedia('(max-width: 850px)').matches
    for (const [key, view] of views) {
      const show = layout === 'original' || key === selected
      if (show && !view.card) view.card = makeCard(view.record)
      view.item.classList.toggle('owed-selected', key === selected)
      view.button.setAttribute('aria-expanded', String(key === selected))
      if (!view.card) continue
      view.card.classList.remove('owed-card-arriving')
      view.card.hidden = !show
      ;(layout === 'original' || inline || !show ? view.item : slot).append(view.card)
    }
    slot.querySelector('.owed-selection-hint')?.remove()
    pendingCards()
  }
  pendingCards()
}

// ---- money brought from Squeegee (0043) ------------------------------------------------------------------------
// One tile per account still owing (the visits' tile, with the account's own chase ladder), a greyed line for each
// direct debit Squeegee had still collecting (Liam Q4: "Collecting £22, don't chase", then Collected or Failed), and
// a box for each "Paid cash, amount not recorded" an older app left.
function renderAccount(a: BroughtAccount, container: HTMLElement, today: string): void {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const actionCurrent = (): boolean => onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition
  clear(container)
  container.classList.add('owed-tile')
  const who = a.customer_name ?? `#${a.customer_id}`
  const first = firstName(a.customer_name)
  container.append(el('a', { href: `#/customer/${a.customer_id}`, class: 'owed-who' }, who))
  // Nothing left but a direct debit still collecting: that figure, greyed, rather than £0.00.
  container.append(a.left_pence > 0 || !a.collecting_pence
    ? el('span', { class: 'owed-amt owed-amt-fixed' }, money(a.left_pence))
    : el('span', { class: 'owed-amt owed-amt-fixed owed-amt-collecting' }, money(a.collecting_pence)))
  const meta = el('div', { class: 'owed-meta' })
  meta.append(el('span', { class: 'owed-from' }, 'Brought from Squeegee'), el('span', { class: 'owed-dot' }, '·'), el('span', {}, `as of ${dayLabel(a.as_of.slice(0, 10))}`))
  // The address on its own line: no separator left hanging at the end of the first.
  if (a.customer_address) meta.append(el('span', { class: 'owed-addr owed-addr-line' }, a.customer_address))
  const chaseFields = { chase_count: a.chase_count, chased: a.chase_count > 0 ? 1 : 0, chased_on: a.chased_on }
  const chip = a.left_pence > 0 ? owedChip({ ...chaseFields, payment_status: 'unpaid' } as unknown as Clean, today, shortDate) : null
  if (chip) meta.append(el('span', { class: `owed-chip owed-chip-${chip.kind}` }, chip.text))
  container.append(meta)
  for (const k of a.collections) container.append(collectingRow(k))

  const actions = el('div', { class: 'owed-acts' })
  const n = a.chase_count
  const stage = a.chaseable_pence > 0 ? accountStage(a, today) : null
  const tel = telHref(a.customer_phone ?? null)
  const reload = (): void => { if (actionCurrent()) void load() }
  // With Undo, as a visit's chase (bug hunt 8 Oct 2026, owed-today #10): the count and its day as they were.
  const chase = (count: number, on: string | null, button: HTMLElement, how = ''): void => {
    button.setAttribute('disabled', 'disabled')
    void mutate({ label: `Chase: ${who} (from Squeegee)`, method: 'PATCH', path: `/api/brought-accounts/${a.id}`, body: { version: a.version, chase_count: count, chased_on: on }, onAck: reload, onConflict: reload }).then((ack) => {
      alert4xx(ack)
      if (!actionCurrent()) return
      undoPatch({ text: count > n ? `Counted a chase${how ? ` ${how}` : ''} to ${who}` : `Took back the last chase to ${who}`, label: `Undo chase: ${who} (from Squeegee)`, path: `/api/brought-accounts/${a.id}`, ack, version: a.version,
        body: { chase_count: a.chase_count, chased_on: a.chased_on ?? null }, after: reload })
    })
  }
  if (!classic && a.chaseable_pence > 0) debtSteps(container, { ...a, chased: n > 0 ? 1 : 0, payment_status: 'unpaid' }, `/api/brought-accounts/${a.id}`, today)
  if (stage === 'send') {
    const rung = nextRung(n, 0)
    const target = { id: a.customer_id, name: who, version: a.customer_version ?? 1, message_by: a.customer_message_by ?? null, phone: a.customer_phone ?? null, email: a.customer_email ?? null }
    const message = accountChaseMessage(a.customer_name, a.chaseable_pence, rung)
    const subject = `Payment reminder from ${businessSettings().business_name}`
    // The same button words and "I chased them myself" as a visit's chase (owed-today #10).
    const via = (way: string): string => (way === 'text' ? 'by text' : way === 'email' ? 'by email' : 'on WhatsApp')
    const control = sendControl(target, reminderDrafts.get(`account:${a.id}`) ?? message, subject, {
      label: (way) => (n === 0 ? `Chase ${via(way)}` : n === 1 ? 'Second chase' : 'Third chase'),
      manualSend: true, className: `btn-whatsapp owed-chase${n > 0 ? ' owed-chase-again' : ''}`,
      changed: reload,
      onConflict: reload,
    })
    const link = control.link
    if (link) {
      const recordChase = (e: MouseEvent): void => { if ((e.type === 'click' && e.button !== 0) || (e.type === 'auxclick' && e.button !== 1)) return; if (link.dataset.busy) { e.preventDefault(); return }; link.dataset.busy = '1'; chase(n + 1, today, link, via(control.send.way)) }
      bindEmailSend(link, () => link.dataset.busy ? null : {
        href: link.getAttribute('href') ?? '', sent: () => { link.dataset.busy = '1'; chase(n + 1, today, link, via(control.send.way)) },
      })

    }
    if (classic) actions.append(control.root)
    if (!classic) {
      container.append(reminderPreview(`account:${a.id}`, message, control, target, subject), el('div', { class: 'owed-send-pin' }, control.root))
      const myself = el('button', { type: 'button', class: 'link-button owed-myself' }, 'I chased them myself')
      myself.addEventListener('click', () => chase(n + 1, today, myself, 'by hand'))
      placeMyself(control.root, myself, actions)
    }
  } else if (stage === 'call' && tel) {
    actions.append(debtCall(tel, first, `/api/brought-accounts/${a.id}`, a.version))
  }
  // Paid: all of what is left, in cash or by bank, like a visit.
  if (a.left_pence > 0) {
    const pair = el('div', { class: 'owed-pair' })
    pair.append(el('span', { class: 'owed-pair-label' }, 'Paid'))
    for (const m of ['cash', 'bank'] as const) {
      const btn = el('button', { type: 'button', class: 'owed-pair-btn', 'aria-label': m === 'cash' ? 'Paid cash' : 'Paid bank' }, m === 'cash' ? 'Cash' : 'Bank')
      paidLook(btn, m)
      btn.addEventListener('click', () => payAccount(a, m, container, btn))
      pair.append(btn)
    }
    actions.append(pair)
  }
  // More: part paid, correct the figure, write off, their page.
  const more = el('button', { type: 'button', class: 'owed-more', 'aria-haspopup': 'true', 'aria-expanded': 'false' }, 'More ▾')
  const menu = el('div', { class: 'owed-menu', role: 'menu' })
  menu.hidden = true
  const editor = el('div', { class: 'owed-inline' })
  editor.hidden = true
  const openEditor = (kind: 'part' | 'correct'): void => {
    closeMenu()
    clear(editor)
    editor.hidden = false
    const amount = el('input', { type: 'text', inputmode: 'decimal', class: 'owed-inline-amt', placeholder: '0.00', 'aria-label': kind === 'part' ? `Amount paid, in ${currencyWord()}` : `Change, in ${currencyWord()}` })
    const reason = el('input', { type: 'text', class: 'owed-inline-reason', placeholder: 'Why it changes', 'aria-label': 'Why the figure changes', maxlength: '200' })
    const err = el('span', { class: 'form-error', role: 'alert' })
    const go = el('button', { type: 'button', class: 'btn-primary' }, kind === 'part' ? 'Save part paid' : 'Save the change')
    const cancel = el('button', { type: 'button' }, 'Cancel')
    cancel.addEventListener('click', () => { editor.hidden = true })
    let method: 'cash' | 'bank' = 'cash'
    const ways = el('span', { class: 'owed-inline-ways' })
    if (kind === 'part') {
      for (const m of ['cash', 'bank'] as const) {
        const b = el('button', { type: 'button', class: `pay-pill${m === method ? ' pay-on' : ''}`, 'aria-pressed': String(m === method) }, m === 'cash' ? 'Cash' : 'Bank')
        b.addEventListener('click', () => { method = m; for (const x of ways.querySelectorAll('button')) { const on = x === b; x.classList.toggle('pay-on', on); x.setAttribute('aria-pressed', String(on)) } })
        ways.append(b)
      }
    }
    go.addEventListener('click', () => {
      let pence: number | null
      const raw = amount.value.trim()
      try { pence = parseMoney(kind === 'correct' && raw.startsWith('-') ? raw.slice(1) : raw); if (pence !== null && kind === 'correct' && raw.startsWith('-')) pence = -pence }
      catch (error) { err.textContent = (error as Error).message; return }
      if (pence === null || pence === 0 || Math.abs(pence) > 10_000_000 || (kind === 'part' && pence < 0)) { err.textContent = `Type an amount in ${currencyWord()}, for example 12 or 12.50.`; return }
      if (kind === 'correct' && !reason.value.trim()) { err.textContent = 'Say why the figure changes.'; return }
      go.setAttribute('disabled', 'disabled')
      void mutate(kind === 'part'
        ? { label: `Part paid: ${who} (from Squeegee)`, method: 'POST', path: `/api/brought-accounts/${a.id}/pay`, body: { method, amount_pence: pence }, onAck: reload, onConflict: reload }
        : { label: `Correct: ${who} (from Squeegee)`, method: 'POST', path: `/api/brought-accounts/${a.id}/correct`, body: { amount_pence: pence, reason: reason.value.trim() }, onAck: reload, onConflict: reload },
      ).then((ack) => { if (!ack.ok) go.removeAttribute('disabled'); alert4xx(ack) })
    })
    editor.append(
      el('span', { class: 'owed-inline-label' }, kind === 'part' ? 'Paid' : 'Change by (minus to lower)'),
      el('span', { class: 'pay-cash-row' }, el('span', { class: 'pay-cash-pound', 'aria-hidden': 'true' }, currencySymbol()), amount),
      ...(kind === 'part' ? [ways] : [reason]),
      go, cancel, err,
    )
    amount.focus()
  }
  const item = (label: string, sub: string | undefined, fn: () => void, danger = false): void => {
    const b = el('button', { type: 'button', class: `owed-mi${danger ? ' owed-mi-danger' : ''}`, role: 'menuitem' }, menuText(label, sub))
    b.addEventListener('click', fn)
    menu.append(b)
  }
  if (a.left_pence > 0) item('Part paid', 'Some of it, in cash or by bank', () => openEditor('part'))
  item('Correct the figure', 'With the reason, kept in their history', () => openEditor('correct'))
  if (n > 0) item("I didn't send that chase", 'Puts the Chase button back', () => chase(n - 1, null, more))
  menu.append(el('a', { href: `#/customer/${a.customer_id}`, class: 'owed-mi', role: 'menuitem' }, menuText(`Open ${first || who}'s page`)))
  if (a.left_pence > 0) {
    menu.append(el('div', { class: 'owed-menu-sep' }))
    item(`Write off ${money(a.left_pence)}`, 'It is not coming. Kept in their history', () => {
      closeMenu()
      if (!window.confirm(`Write off the ${money(a.left_pence)} ${who} owes from Squeegee?`)) return
      void mutate({ label: `Write off: ${who} (from Squeegee)`, method: 'POST', path: `/api/brought-accounts/${a.id}/write-off`, body: {}, onAck: reload, onConflict: reload }).then(alert4xx)
    }, true)
  }
  more.addEventListener('click', (e) => {
    e.stopPropagation()
    const wasOpen = openMenu?.menu === menu
    closeMenu()
    if (wasOpen) return
    menu.hidden = false
    more.setAttribute('aria-expanded', 'true')
    more.textContent = 'More ▴'
    openMenu = { menu, close: () => { menu.hidden = true; more.setAttribute('aria-expanded', 'false'); more.textContent = 'More ▾' } }
    placeMenu(menu, more)
  })
  if (tel && !classic && !actions.querySelector('.owed-call-btn')) actions.append(el('a', { href: tel, class: 'owed-call-btn', 'aria-label': `Call ${who}` }, callLabel(first)))
  actions.append(more, menu)
  if (!classic) pairUp(actions)
  container.append(actions, editor)
}

// Paid on an account brought from Squeegee: all of what is left, from its card or from its row on a phone.
function payAccount(a: BroughtAccount, m: 'cash' | 'bank', host: HTMLElement, btn: HTMLElement, from: HTMLElement = btn): void {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const actionCurrent = (): boolean => onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition
  const who = a.customer_name ?? `#${a.customer_id}`
  btn.setAttribute('disabled', 'disabled')
  host.classList.add('cell-pending')
  const takeBack = paidMoment({ host, from, way: m, line: host.querySelector<HTMLElement>('.owed-line'), mark: host.querySelector<HTMLElement>('.owed-avatar') })
  const amountEl = host.querySelector<HTMLElement>('.owed-queue-money') ?? from
  void mutate({ label: `Paid ${m}: ${who} (from Squeegee)`, method: 'POST', path: `/api/brought-accounts/${a.id}/pay`, body: { method: m, amount_pence: a.left_pence },
    onAck: () => { if (!actionCurrent()) return; flyToPaidToday(amountEl, a.left_pence); justPaidAt = Date.now(); host.classList.add('owed-paying'); countFromPence = combinedTotal(); const gen = routeGen; void slideAway(host, 380).then(() => { if (gen === routeGen) void load() }) },
    onConflict: () => { if (actionCurrent()) void load() } }).then((ack) => {
    if (!actionCurrent()) return
    if (!ack.ok && ack.status >= 400 && ack.status < 500) { btn.removeAttribute('disabled'); host.classList.remove('cell-pending', 'owed-paying'); takeBack() }
    alert4xx(ack)
  })
}

function collectingRow(k: { id: number; amount_pence: number; on_date: string | null; version: number }): HTMLElement {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const reload = (): void => { if (onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition) void load() }
  const row = el('div', { class: 'owed-collecting' })
  row.append(el('span', {}, `${collectingWords(k, money)}${k.on_date ? ` · since ${shortDate(k.on_date)}` : ''}`))
  const answers = el('span', { class: 'owed-collecting-acts' })
  row.append(answers)
  for (const state of ['collected', 'failed'] as const) {
    const b = el('button', { type: 'button', class: 'owed-collecting-btn' }, state === 'collected' ? 'Collected' : 'Failed')
    b.addEventListener('click', () => {
      b.setAttribute('disabled', 'disabled')
      void mutate({ label: `Direct debit ${state}`, method: 'POST', path: `/api/brought-collections/${k.id}`, body: { state }, onAck: reload, onConflict: reload }).then(alert4xx)
    })
    answers.append(b)
  }
  return row
}

function confirmCashRow(c: UnconfirmedCash): HTMLElement {
  const actionGeneration = routeGen, actionPartition = queuePartition()
  const reload = (): void => { if (onOwed() && routeGen === actionGeneration && queuePartition() === actionPartition) void load() }
  const row = el('div', { class: 'owed-inline owed-cash-row' })
  const amount = el('input', { type: 'text', inputmode: 'decimal', class: 'owed-inline-amt', placeholder: '0.00', 'aria-label': `Cash taken from ${c.customer_name ?? 'them'}, in ${currencyWord()}` })
  const go = el('button', { type: 'button', class: 'btn-primary' }, 'Confirm')
  const err = el('span', { class: 'form-error', role: 'alert' })
  go.addEventListener('click', () => {
    let pence: number | null
    try { pence = parseMoney(amount.value) } catch (error) { err.textContent = (error as Error).message; return }
    if (pence === null || pence < 0 || pence > 10_000_000) { err.textContent = 'Type what was taken, for example 19 (0 if nothing).'; return }
    go.setAttribute('disabled', 'disabled')
    void mutate({ label: `Cash confirmed: ${c.customer_name ?? ''}`, method: 'POST', path: `/api/money-receipts/${c.id}/confirm`, body: { amount_pence: pence }, onAck: reload, onConflict: reload })
      .then((ack) => { if (!ack.ok) go.removeAttribute('disabled'); alert4xx(ack) })
  })
  row.append(
    el('a', { href: `#/customer/${c.customer_id ?? ''}`, class: 'owed-who' }, c.customer_name ?? 'Customer'),
    el('span', { class: 'owed-sub' }, `${c.visit_date ? dayLabel(c.visit_date) : dayLabel(c.received_on)}${c.visit_pence != null ? ` · ${money(c.visit_pence)} clean` : ''}`),
    el('span', { class: 'pay-cash-row' }, el('span', { class: 'pay-cash-pound', 'aria-hidden': 'true' }, currencySymbol()), amount),
    go, err,
  )
  return row
}

// ---- Original: the Owed screen as it was on 5 Oct 2026 (before the redesign) ---------------------------------
// The layout, words and order are the old screen's (e4db3cc). Rows, accounts, cash and direct-debit lines are drawn
// by today's renderers above, so every money action keeps its current safeguards (partial cash, payment Undo,
// stale-screen guards); only the newer extras (price facts box, message preview) stay in New.

const UNRECORDED_NOTE =
  'These are not chases. The payment may well have been taken and never ticked off. Tap Cash or Bank under Paid, or "Not paid", on a row to clear it. "Not paid" moves it into the chase list.'

function classicRowList(rows: Clean[], today: string): HTMLElement {
  const list = el('div', { class: 'clean-list' })
  for (const cl of rows) {
    const row = el('div', {})
    if (cl.chased) row.classList.add('owed-chased')
    renderRow(cl, row, today)
    list.append(row)
  }
  return list
}

function renderClassic(data: OwedPayload, root: HTMLElement, fresh = false): void {
  closeMenu()
  clear(root)
  landOn = null
  const page = el('div', { class: 'owed-classic' }); root.append(page)
  const today = todayISO()
  if (fresh) setOwedBadge(data, today)
  const head = el('div', { class: 'owed-head' })
  // With money brought from Squeegee (0043) the headline is the visits and the accounts together.
  const brought = data.brought ?? null
  const headline = data.total_pence + (brought?.total_pence ?? 0)
  const total = el('span', { class: 'owed-total' }, money(headline))
  head.append(total)
  // After a Paid tap the headline counts down from the figure it showed.
  const from = countFromPence
  countFromPence = null
  if (from !== null && from !== headline) {
    const to = headline
    countBetween(total, (k) => money(Math.round(from + (to - from) * k)))
  }
  const sub = `owed across ${data.count} clean${data.count === 1 ? '' : 's'}${brought && brought.count ? ` and ${brought.count} account${brought.count === 1 ? '' : 's'} from Squeegee` : ''}`
  head.append(el('span', { class: 'owed-sub' }, sub))
  // An unpriced unpaid clean is money owed of an unknown amount — it must not
  // silently read as £0 inside the headline figure.
  if (data.unpriced > 0) {
    head.append(
      el('span', { class: 'owed-warn' }, `+ ${data.unpriced} with no price set, so the real figure is higher`),
    )
  }
  page.append(head)
  // Bank transfers ticked off from a statement (#/bank, 0030).
  page.append(el('div', { class: 'owed-bank' }, el('a', { href: '#/bank', class: 'grid-list-btn' }, 'Tick off from a bank statement')))

  // Filter chips, the same control Messages carries: which pile am I looking
  // at. Counts are the whole payload, so they stay true while searching.
  const counts = owedCounts(data, today)
  const chips = el('div', { class: 'rebook-chips owed-chips', role: 'group', 'aria-label': 'Filter owed' })
  for (const { value, label } of OWED_FILTERS) {
    const on = value === filterMode
    const chip = el('button', { type: 'button', class: `rebook-chip${on ? ' rebook-chip-on' : ''}`, 'aria-pressed': on ? 'true' : 'false' }, `${label} ${counts[value]}`)
    chip.addEventListener('click', () => { filterMode = value; render(data, root) })
    chips.append(chip)
  }
  page.append(chips)

  // Search: name, address, postcode, phone or note. The totals above stay
  // whole-list; the rows below are the ones that match, within the chip.
  const bar = el('div', { class: 'screen-bar' })
  const search = el('input', { type: 'search', class: 'search-box', placeholder: 'Name, address or phone', 'aria-label': 'Search owed' })
  search.value = filterText
  search.addEventListener('input', () => {
    const at = search.selectionStart ?? search.value.length
    filterText = search.value
    render(data, root)
    const again = root.querySelector('input[type=search]') as HTMLInputElement | null
    if (again) { again.focus(); again.setSelectionRange(at, at) }
  })
  bar.append(search)
  const q = filterText.trim().toLowerCase()
  const { rows, unrecorded: unShown } = filterOwed(data, filterMode, filterText, today)
  if (q) bar.append(el('span', { class: 'muted' }, `${rows.length + unShown.length} of ${counts[filterMode]} shown`))
  page.append(bar)

  // Money brought from Squeegee (0043): the accounts still owing, and any cash an older app could not put a figure on.
  const accountsShown = brought && filterMode !== 'unrecorded' ? classicBrought(brought, page, today, q, filterMode) : 0

  // The chase list. "Not recorded" has no chase list at all — its rows are
  // the second bucket, rendered below.
  if (filterMode !== 'unrecorded') {
    // Visits only: accounts from Squeegee are counted in the chips and drawn above (Astra r2, finding 13).
    const inBucket = filterMode === 'all' ? data.cleans.length : owedCounts({ ...data, brought: undefined }, today)[filterMode]
    if (inBucket === 0) {
      if (!accountsShown && !(brought && q)) page.append(el('p', { class: 'empty-note' }, owedEmptyText(filterMode)))
    } else if (rows.length === 0) {
      if (!accountsShown) page.append(el('p', { class: 'empty-note' }, 'No one owed matches that.'))
    } else {
      page.append(classicRowList(rows, today))
    }
  }

  // Second bucket: done work nobody ever marked paid OR unpaid. Separate,
  // because chasing it would mean dunning people who may already have paid —
  // but leaving it off the screen is how ~£2.9k went unnoticed.
  const un = data.unrecorded
  if (filterMode === 'all' && un && un.count > 0) {
    const box = el('details', { class: 'owed-unrecorded' })
    const sum = el('summary', {})
    sum.append(
      el('strong', {}, `${money(un.total_pence)} not recorded`),
      el('span', { class: 'owed-sub' }, ` · ${un.count} finished clean${un.count === 1 ? '' : 's'} where nobody logged whether the money arrived`),
    )
    box.append(sum)
    box.append(el('p', { class: 'owed-note' }, UNRECORDED_NOTE))
    if (q) box.open = unShown.length > 0
    box.append(unShown.length ? classicRowList(unShown, today) : el('p', { class: 'empty-note' }, 'No match here.'))
    page.append(box)
  } else if (filterMode === 'unrecorded') {
    // Picked on purpose, so it is the list, not a fold: the same headline the
    // summary carried, the explainer once, then the rows.
    if (!un || un.count === 0) {
      page.append(el('p', { class: 'empty-note' }, owedEmptyText('unrecorded')))
    } else {
      const line = el('p', { class: 'owed-unrecorded-head' })
      line.append(
        el('strong', {}, `${money(un.total_pence)} not recorded`),
        el('span', { class: 'owed-sub' }, ` · ${un.count} finished clean${un.count === 1 ? '' : 's'} where nobody logged whether the money arrived`),
      )
      page.append(line)
      page.append(el('p', { class: 'owed-note' }, UNRECORDED_NOTE))
      page.append(unShown.length ? classicRowList(unShown, today) : el('p', { class: 'empty-note' }, 'No one owed matches that.'))
    }
  }
}

// Money brought from Squeegee (0043), as Original showed it: one tile per account still owing, the direct debits
// Squeegee had still collecting, and a box for each "Paid cash, amount not recorded" an older app left.
function classicBrought(b: OwedBrought, root: HTMLElement, today: string, q: string, filter: OwedFilter): number {
  const match = (s: string | null | undefined): boolean => !q || (s ?? '').toLowerCase().includes(q)
  // The chips pick accounts by their own place on the ladder, as they pick visits (owed-view.ts accountStage).
  const accounts = b.accounts.filter((a) => (filter === 'all' || accountStage(a, today) === filter) && (match(a.customer_name) || match(a.customer_address) || match(a.customer_phone)))
  if (accounts.length) {
    const box = el('section', { class: 'owed-brought', 'aria-label': 'Owed from Squeegee' })
    box.append(el('h3', { class: 'owed-brought-head' }, el('span', {}, 'From Squeegee'), el('span', { class: 'owed-sub' }, ` · ${money(b.total_pence)} on ${b.count} account${b.count === 1 ? '' : 's'}`)))
    const list = el('div', { class: 'owed-list' })
    for (const a of accounts) {
      const row = el('div', { class: 'owed-row owed-account' })
      renderAccount(a, row, today)
      list.append(row)
    }
    box.append(list)
    root.append(box)
  }
  const cash = filter === 'all' ? b.unconfirmed_cash.filter((c) => match(c.customer_name)) : []
  if (cash.length) {
    const box = el('section', { class: 'owed-brought owed-cash-confirm', 'aria-label': 'Cash to confirm' })
    box.append(el('h3', { class: 'owed-brought-head' }, el('span', {}, 'Paid cash, amount not recorded'), el('span', { class: 'owed-sub' }, ' · taken on a phone running an older app. Type what was taken.')))
    for (const c of cash) box.append(confirmCashRow(c))
    root.append(box)
  }
  return accounts.length + cash.length
}

async function load(): Promise<void> {
  if (!onOwed()) return
  const gen = routeGen
  const sequence = ++requestSequence
  const root = document.querySelector<HTMLElement>('#screen')!
  try {
    // Several jobs on: the round's jobs too, so a visit of a customer with more than one job says which.
    const [result, invoices, , wa] = await Promise.all([api<OwedPayload>('/api/owed', 'owed'), invoicesHere() ? api<{ invoices: OpenInvoice[] }>('/api/invoices?open=1').catch(() => null) : Promise.resolve(null), loadRoundJobs().then(() => null),
      getMe()?.waSends ? api<SendAllFacts>('/api/chase-sends').catch(() => null) : Promise.resolve(null)])
    if (gen !== routeGen || sequence !== requestSequence || !onOwed()) return
    waFacts = wa
    openInvoices = invoices?.invoices ?? []
    // Invoiced visits never go back to the visit chase: Owed's own read says which invoice each is on. With the invoices
    // list unreadable, their place says so, with Try again and each invoice to open (Astra's final review F6, 21 Oct 2026;
    // before, they came back under the payment message).
    // Invoices switched off since, too: such a visit is never hidden; it shows here with its invoice's number (Astra's
    // recheck, 21 Oct 2026).
    invoicesOff = !invoicesHere()
    invoicesUnread = !invoices ? [...result.cleans, ...(result.unrecorded?.cleans ?? [])].filter((c) => (c as Clean & OnInvoice).on_invoice) : []
    payload = withoutInvoiced(result)
    lastRead = result
    render(payload, root, true)
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence || !onOwed()) return
    const cached = readCache<OwedPayload>('owed')
    if (cached) {
      // The saved copy still holds visits on invoices; without signal they stay with their invoice rows, never twice.
      payload = openInvoices.length ? withoutInvoiced(cached.data) : cached.data
      render(payload, root)
      loadError(root, (err as Error).message, () => void load(), cached.at)
    } else {
      clear(root)
      loadError(root, (err as Error).message, () => void load())
    }
  }
}

export function mountOwed(): void {
  mountedGeneration = routeGen
  bindLayoutBreakpoint()
  if (mountedPartition !== queuePartition()) { mountedPartition = queuePartition(); selected = null; reminderDrafts.clear(); filterText = ''; filterMode = 'all'; sortMode = 'newest'; payload = null; countFromPence = null }
  if (landOn) selected = landOn
  if (!queueBound) {
    queueBound = true; onQueueChange(pendingCards)
    window.addEventListener('roundly:queued-landed', (e) => {
      if (!document.querySelector('.owed-page, .owed-classic') || mountedPartition !== queuePartition()) return
      const detail = (e as CustomEvent<{ path: string; row?: Clean & { _payment_effect?: PaymentEffect } }>).detail
      if (/^\/api\/cleans\/\d+$/.test(detail.path) && detail.row?._payment_effect) paymentUndo({ ok: true, status: 200, row: detail.row }, 'visit', detail.row.id, 'Payment saved · review the updated balance')
    })
  }
  watchMenus()
  void load()
}

export function owedOnDataChanged(): void {
  void load()
}

// "I sent it myself" beside "Add number" when there is no number to send to (Liam, 28 Oct 2026: "should that i did it
// myself button go next to the add number button", "do it"): the other way to deal with the same step, on the same row,
// never a narrow button on a line of its own. With a number to send to it stays under the send button.
function placeMyself(sendRow: HTMLElement, myself: HTMLElement, actions: HTMLElement): void {
  const add = sendRow.querySelector<HTMLElement>('.send-add')
  if (add) { myself.classList.add('owed-myself-beside'); add.before(myself) } else actions.append(myself)
}

// The Diary's "Done · not paid" (and anywhere else that names one owed visit) opens Owed on that visit, picked out,
// with the chips and search cleared so nothing hides it (docs/DIARY-AND-MESSAGES.md, Owed).
export function openOwedFor(visitId: number): void {
  filterMode = 'all'
  filterText = ''
  selected = landOn = `visit:${visitId}`
  if (window.location.hash === '#/owed') void load()
  else window.location.hash = '#/owed'
}
